import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react'
import { colorPersistence } from '../../../utils/colorPersistence'
import { 
  Globe, Music, Youtube, FileText, ShoppingCart, User, Book,
  Upload, Link, Package, Calendar, Building, Hash, Clock,
  Mail, Download, Droplet, Plus, Trash2, MapPin, Award,
  Instagram, Facebook, Linkedin, ExternalLink, Palette,
  RefreshCw, CheckCircle, XCircle, Save, Info, ArrowLeft, ArrowRight,
  ChevronRight, ChevronDown, QrCode, Lock, X, Zap, Shield, Play, Sparkles, Video,
  Type, Settings
} from 'lucide-react'
import { XIcon } from '../../icons/XIcon'
import { useAuth } from '../../../AuthProvider'
import { SplitFormLayout } from './shared/SplitFormLayout'
import { UnifiedPreview } from '../previews/UnifiedPreview'
import { BrandingSelector } from '../BrandingSelector'
import { ColorSelector } from '../ColorSelector'
import { useDebounce } from '../../../hooks/useDebounce'
import QRStylePanel from '../QRStylePanel'
import { QRStyleConfig } from '../QRStyleModal'
import QRCodeStyling from 'qr-code-styling'
import { generateQRCode } from '../../../utils/qrCodeHelper'
import { getShopConfig, getShopLogo, isLocalShop } from '../../../utils/shopLogos'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../ui/tabs'

// Standardized font options for consistent user experience
const FONT_OPTIONS = {
  Modern: [
    { value: 'Inter', label: 'Inter' },
    { value: 'Roboto', label: 'Roboto' },
    { value: 'Open Sans', label: 'Open Sans' },
    { value: 'Lato', label: 'Lato' },
    { value: 'Montserrat', label: 'Montserrat' },
    { value: 'Source Sans Pro', label: 'Source Sans Pro' },
    { value: 'Nunito', label: 'Nunito' },
    { value: 'Poppins', label: 'Poppins' },
  ],
  Elegant: [
    { value: 'Merriweather', label: 'Merriweather' },
    { value: 'Playfair Display', label: 'Playfair Display' },
    { value: 'Lora', label: 'Lora' },
    { value: 'Georgia', label: 'Georgia' },
    { value: 'Crimson Text', label: 'Crimson Text' },
    { value: 'Libre Baskerville', label: 'Libre Baskerville' },
    { value: 'Cinzel', label: 'Cinzel' },
    { value: 'Cormorant Garamond', label: 'Cormorant Garamond' },
    { value: 'Marcellus', label: 'Marcellus' },
    { value: 'Forum', label: 'Forum' },
    { value: 'Rozha One', label: 'Rozha One' },
    { value: 'Gilda Display', label: 'Gilda Display' },
    { value: 'Yeseva One', label: 'Yeseva One' },
    { value: 'Elsie', label: 'Elsie' },
    { value: 'Abril Fatface', label: 'Abril Fatface' },
    { value: 'Vidaloka', label: 'Vidaloka' },
  ],
}

// Helper function to load Google Fonts for dropdown preview
const loadFontForDropdown = () => {
  const allFonts = [...FONT_OPTIONS.Modern, ...FONT_OPTIONS.Elegant]
  const fontLinks = allFonts
    .filter(font => font.value !== 'Georgia' && font.value !== 'Inter') // Georgia is system font, Inter is already loaded
    .map(font => {
      const fontName = font.value.replace(/ /g, '+') // Replace all spaces with +
      return `https://fonts.googleapis.com/css2?family=${fontName}:wght@400;600;700&display=swap`
    })
  
  // Check if fonts are already loaded
  const existingLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
  
  fontLinks.forEach(href => {
    if (!existingLinks.some(link => link.getAttribute('href') === href)) {
      const link = document.createElement('link')
      link.href = href
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
  })
}

// Helper component to render font options consistently with font preview
const FontOptions: React.FC = () => {
  React.useEffect(() => {
    loadFontForDropdown()
  }, [])
  
  return (
    <>
      <optgroup label="Modern">
        {FONT_OPTIONS.Modern.map(font => (
          <option 
            key={font.value} 
            value={font.value}
            style={{ fontFamily: font.value }}
          >
            {font.label}
          </option>
        ))}
      </optgroup>
      <optgroup label="Elegant">
        {FONT_OPTIONS.Elegant.map(font => (
          <option 
            key={font.value} 
            value={font.value}
            style={{ fontFamily: font.value }}
          >
            {font.label}
          </option>
        ))}
      </optgroup>
    </>
  )
}

interface UnifiedQRFormProps {
  type: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample'
  onTypeChange?: (type: 'website' | 'spotify' | 'youtube' | 'pdf' | 'multishop' | 'author' | 'sample') => void
  onSubmit: (data: any, bookId?: string, campaignId?: string, slug?: string) => void
  initialData?: any
  books?: any[]
  campaigns?: any[]
  selectedBookId?: string
  selectedCampaignId?: string
  onSelectedBookIdChange?: (bookId: string) => void
  onSelectedCampaignIdChange?: (campaignId: string) => void
  currentAccountSlug?: string
  brandingType?: 'beyondpages' | 'custom' | 'none'
  onBrandingTypeChange?: (type: 'beyondpages' | 'custom' | 'none') => void
  userBranding?: any
  customColors?: {
    primaryColor: string
    secondaryColor: string
    textColor: string
    wrapperColor?: string
    wrapperOpacity?: number
  }
  onCustomColorsChange?: (colors: { primaryColor: string; secondaryColor: string; textColor: string; wrapperColor?: string; wrapperOpacity?: number }) => void
  colorMode?: 'gradient' | 'structured'
  onColorModeChange?: (mode: 'gradient' | 'structured') => void
  canUploadPDF?: boolean
  isEditMode?: boolean
  editingLink?: {
    slug: string
    redirect: string
  }
  hideDesignTab?: boolean
  hideContentTab?: boolean
  defaultTab?: 'content' | 'design'
  onBack?: () => void
}

// Common form data structure
interface CommonFormData {
  title: string
  description?: string
  target_url?: string
  pdf_url?: string
  slug?: string
}

export const UnifiedQRForm: React.FC<UnifiedQRFormProps> = ({
  type,
  onTypeChange,
  onSubmit,
  initialData,
  books = [],
  campaigns = [],
  selectedBookId,
  selectedCampaignId,
  onSelectedBookIdChange,
  onSelectedCampaignIdChange,
  currentAccountSlug = '',
  brandingType: propBrandingType,
  onBrandingTypeChange,
  userBranding: propUserBranding,
  customColors: propCustomColors,
  onCustomColorsChange,
  colorMode: propColorMode,
  onColorModeChange,
  canUploadPDF = true,
  isEditMode = false,
  editingLink,
  hideDesignTab = false,
  hideContentTab = false,
  defaultTab = 'content',
  onBack
}) => {
  const { session } = useAuth()
  
  // Debugging: Log initialData on every render
  console.log('🎯 UnifiedQRForm RENDER - type:', type)
  console.log('🎯 UnifiedQRForm RENDER - initialData:', initialData)
  console.log('🎯 UnifiedQRForm RENDER - initialData keys:', initialData ? Object.keys(initialData) : 'none')
  console.log('🎯 UnifiedQRForm RENDER - isEditMode:', isEditMode)
  
  // Log when component mounts/remounts
  useEffect(() => {
    console.log('🔍 UnifiedQRForm - Component mounted/remounted')
    console.log('🔍 UnifiedQRForm - Props:', {
      type,
      isEditMode,
      initialData,
      hasInitialData: !!initialData,
      initialDataKeys: initialData ? Object.keys(initialData) : [],
      pdf_url: initialData?.pdf_url,
      metadata: initialData?.metadata
    })
    
    return () => {
      console.log('🔍 UnifiedQRForm - Component unmounting')
    }
  }, [])
  
  const [selectedBook, setSelectedBook] = useState<any>(null)
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null)
  // Local selection state for assignment (IDs), since props are read-only
  const [localSelectedBookId, setLocalSelectedBookId] = useState<string>(selectedBookId || '')
  const [localSelectedCampaignId, setLocalSelectedCampaignId] = useState<string>(selectedCampaignId || '')
  const [customSlug, setCustomSlug] = useState(editingLink?.slug || '')
  const [uploadingFile, setUploadingFile] = useState(false)
  const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null)
  const [checkingSlug, setCheckingSlug] = useState(false)
  
  // Branding state - use props if provided, otherwise local state
  const [localBrandingType, setLocalBrandingType] = useState<'beyondpages' | 'custom' | 'none'>(() => {
    // Check global persistence first (survives auth state changes)
    if (!isEditMode) {
      const persistedState = colorPersistence.getState()
      if (persistedState?.brandingType) {
        console.log('🎨 UnifiedQRForm - Using global persisted brandingType:', persistedState.brandingType)
        return persistedState.brandingType
      }
    }
    
    // Initialize from initialData if available
    if (isEditMode) {
      if (initialData?.branding_type) {
        return initialData.branding_type
      } else if (initialData?.metadata?.brandingType) {
        return initialData.metadata.brandingType
      }
    }
    // Check sessionStorage for temporary state (helps with re-renders)
    if (!isEditMode) {
      const saved = sessionStorage.getItem('qr_form_branding_type')
      if (saved && ['beyondpages', 'custom', 'none'].includes(saved)) {
        return saved as 'beyondpages' | 'custom' | 'none'
      }
    }
    return 'beyondpages'
  })
  const brandingType = propBrandingType !== undefined ? propBrandingType : localBrandingType
  
  const [localCustomColors, setLocalCustomColors] = useState(() => {
    console.log('🎨 UnifiedQRForm - Initializing localCustomColors state')
    console.log('🎨 UnifiedQRForm - isEditMode:', isEditMode)
    console.log('🎨 UnifiedQRForm - propCustomColors:', propCustomColors)
    
    // If props provide colors, use them
    if (propCustomColors) {
      console.log('🎨 UnifiedQRForm - Using propCustomColors:', propCustomColors)
      return propCustomColors
    }
    
    // Check global persistence (survives auth state changes)
    if (!isEditMode) {
      const persistedState = colorPersistence.getState()
      if (persistedState?.customColors) {
        console.log('🎨 UnifiedQRForm - Using global persisted colors:', persistedState.customColors)
        return persistedState.customColors
      }
    }
    
    // Initialize from initialData if available
    if (isEditMode) {
      if (initialData?.custom_colors) {
        console.log('🎨 UnifiedQRForm - Using initialData.custom_colors:', initialData.custom_colors)
        return {
          primaryColor: initialData.custom_colors.primaryColor || '#93C5FD',
          secondaryColor: initialData.custom_colors.secondaryColor || '#DBEAFE',
          textColor: initialData.custom_colors.textColor || '#1F2937',
          wrapperColor: initialData.custom_colors.wrapperColor || '#5B8DD9',
          wrapperOpacity: initialData.custom_colors.wrapperOpacity || 1
        }
      } else if (initialData?.metadata?.customColors) {
        console.log('🎨 UnifiedQRForm - Using initialData.metadata.customColors:', initialData.metadata.customColors)
        return {
          primaryColor: initialData.metadata.customColors.primaryColor || '#93C5FD',
          secondaryColor: initialData.metadata.customColors.secondaryColor || '#DBEAFE',
          textColor: initialData.metadata.customColors.textColor || '#1F2937',
          wrapperColor: initialData.metadata.customColors.wrapperColor || '#5B8DD9',
          wrapperOpacity: initialData.metadata.customColors.wrapperOpacity || 1
        }
      }
    }
    // Check sessionStorage for temporary state (helps with re-renders)
    if (!isEditMode) {
      const saved = sessionStorage.getItem('qr_form_custom_colors')
      console.log('🎨 UnifiedQRForm - SessionStorage qr_form_custom_colors:', saved)
      if (saved) {
        try {
          const parsed = JSON.parse(saved)
          if (parsed.primaryColor && parsed.secondaryColor && parsed.textColor) {
            console.log('🎨 UnifiedQRForm - Using sessionStorage colors:', parsed)
            return parsed
          }
        } catch (e) {
          console.log('🎨 UnifiedQRForm - Failed to parse sessionStorage colors:', e)
        }
      }
    }
    console.log('🎨 UnifiedQRForm - Using default colors')
    return {
      primaryColor: '#1F2937',
      secondaryColor: '#93C5FD',
      textColor: '#FFFFFF',
      wrapperColor: '#5B8DD9',
      wrapperOpacity: 1
    }
  })
  
  // Use prop colors if provided, otherwise use local state
  const customColors = propCustomColors || localCustomColors
  
  // Define setBrandingType callback after color states are initialized
  const setBrandingType = useCallback((value: 'beyondpages' | 'custom' | 'none') => {
    console.log('🎨 setBrandingType called with:', value)
    console.log('🎨 Current customColors:', customColors)
    console.log('🎨 Current localCustomColors:', localCustomColors)
    console.log('🎨 propCustomColors:', propCustomColors)
    if (!isEditMode) {
      sessionStorage.setItem('qr_form_branding_type', value)
    }
    if (onBrandingTypeChange) {
      onBrandingTypeChange(value)
    } else {
      setLocalBrandingType(value)
    }
  }, [isEditMode, onBrandingTypeChange, customColors, localCustomColors, propCustomColors])
  
  // Update local state when prop changes (to sync with sessionStorage)
  useEffect(() => {
    console.log('🎨 UnifiedQRForm - propCustomColors effect triggered')
    console.log('🎨 UnifiedQRForm - propCustomColors:', propCustomColors)
    console.log('🎨 UnifiedQRForm - localCustomColors:', localCustomColors)
    console.log('🎨 UnifiedQRForm - isEditMode:', isEditMode)
    
    if (propCustomColors && !isEditMode) {
      // Only update if colors actually changed
      if (JSON.stringify(propCustomColors) !== JSON.stringify(localCustomColors)) {
        console.log('🎨 UnifiedQRForm - Colors changed, updating local state')
        setLocalCustomColors(propCustomColors)
        sessionStorage.setItem('qr_form_custom_colors', JSON.stringify(propCustomColors))
      } else {
        console.log('🎨 UnifiedQRForm - Colors are the same, not updating')
      }
    }
  }, [propCustomColors, isEditMode, localCustomColors])
  
  const setCustomColors = useCallback((colors: { primaryColor: string; secondaryColor: string; textColor: string; wrapperColor?: string; wrapperOpacity?: number }) => {
    console.log('🎨 setCustomColors called with:', colors)
    const fullColors = {
      primaryColor: colors.primaryColor,
      secondaryColor: colors.secondaryColor,
      textColor: colors.textColor,
      wrapperColor: colors.wrapperColor || customColors.wrapperColor || '#5B8DD9',
      wrapperOpacity: colors.wrapperOpacity ?? customColors.wrapperOpacity ?? 1
    }
    if (!isEditMode) {
      sessionStorage.setItem('qr_form_custom_colors', JSON.stringify(fullColors))
    }
    if (onCustomColorsChange) {
      onCustomColorsChange(fullColors)
    } else {
      setLocalCustomColors(fullColors)
    }
  }, [isEditMode, onCustomColorsChange, customColors])
  
  // User branding - use prop if provided
  const userBranding = propUserBranding || null
  
  // Clean up sessionStorage when component unmounts or when in edit mode
  useEffect(() => {
    return () => {
      if (!isEditMode) {
        // Clean up sessionStorage on unmount
        sessionStorage.removeItem('qr_form_branding_type')
        sessionStorage.removeItem('qr_form_custom_colors')
        sessionStorage.removeItem('qr_form_color_mode')
      }
    }
  }, [isEditMode])
  
  // Update form data when initialData changes (for wizard navigation)
  useEffect(() => {
    console.log('🔄 UnifiedQRForm useEffect - initialData changed:', {
      initialData,
      isEditMode,
      type,
      hasInitialData: !!initialData,
      initialDataKeys: initialData ? Object.keys(initialData) : []
    })

    if (initialData) {
      console.log('📝 UnifiedQRForm - Updating form data from initialData (isEditMode:', isEditMode, ')')
      console.log('📝 UnifiedQRForm - Full initialData object:', JSON.stringify(initialData, null, 2))

      // Update common data
      const newCommonData = {
        title: initialData.title || '',
        description: initialData.description || '',
        target_url: initialData.target_url || '',
        pdf_url: initialData.pdf_url || '',
        slug: initialData.slug || ''
      }
      console.log('📝 UnifiedQRForm - Setting commonData to:', newCommonData)
      setCommonData(newCommonData)
      
      // Update type-specific data
      const data = initialData
      console.log('📝 UnifiedQRForm - Processing type-specific data for type:', type)
      switch(type) {
        case 'multishop':
          console.log('🛍️ UnifiedQRForm - MultiShop initialization with data:', data)
          console.log('🛍️ UnifiedQRForm - data.bookTitle:', data.bookTitle)
          console.log('🛍️ UnifiedQRForm - data.metadata?.bookTitle:', data.metadata?.bookTitle)
          const multishopData = {
            bookTitle: data.bookTitle || data.metadata?.bookTitle || '',
            author: data.author || data.metadata?.author || '',
            isbn: data.isbn || data.metadata?.isbn || '',
            coverUrl: data.coverUrl || data.metadata?.coverUrl || '',
            publishDate: data.publishDate || data.metadata?.publishDate || '',
            description: data.description || data.metadata?.description || '',
            shops: data.shops || data.metadata?.shops || [{ name: '', url: '', price: '', currency: 'EUR' }],
            primaryShop: data.primaryShop || data.metadata?.primaryShop || '',
            defaultCurrency: data.defaultCurrency || data.metadata?.defaultCurrency || 'EUR',
            fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
            textSizes: data.textSizes || data.metadata?.textSizes || { bookTitle: 'text-2xl', author: 'text-base', shopName: 'text-base', price: 'text-sm', branding: 'text-lg' },
            companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
            openDirectly: data.openDirectly || data.metadata?.openDirectly || false
          }
          console.log('📝 UnifiedQRForm - Setting multishop typeData to:', multishopData)
          setTypeData(multishopData)
          break
        case 'author':
          setTypeData({
            name: data.name || '',
            title: data.title || 'Autor',
            shortBio: data.shortBio || '',
            bio: data.bio || data.fullBio || '',
            profileImageUrl: data.profileImageUrl || '',
            socialMedia: data.socialLinks || { website: '', instagram: '', twitter: '', facebook: '', linkedin: '', tiktok: '' },
            showPlaceholder: false,
            fonts: data.fonts || {},
            textSizes: data.textSizes || {},
            companyPosition: data.companyPosition || 'bottom',
            awards: data.awards || [],
            contact: data.contact || {},
            pressKitUrl: data.pressKitUrl || '',
            videoUrl: data.videoUrl || '',
            newsletterEnabled: data.newsletterEnabled || false
          })
          break
        case 'pdf':
          setTypeData({
            description: data.description || '',
            showDirectly: data.showDirectly !== false,
            openDirectly: data.openDirectly || false,
            password: data.password || null,
            welcomeScreen: data.welcomeScreen || { enabled: false },
            socialLinks: data.socialLinks || {},
            companyPosition: data.companyPosition || 'bottom',
            buttonStyle: data.buttonStyle || 'solid',
            qrCodeName: data.qrCodeName || '',
            fonts: data.fonts || {}
          })
          break
        case 'sample':
          setTypeData({
            title: data.title || '',
            author: data.author || '',
            isbn: data.isbn || '',
            publisher: data.publisher || '',
            publicationDate: data.publicationDate || '',
            genre: data.genre || '',
            description: data.description || '',
            coverUrl: data.coverUrl || '',
            pdfUrl: data.pdf_url || data.pdfUrl || data.metadata?.pdfUrl || '', // Added pdf_url support
            samplePages: data.samplePages || '',
            readingTime: data.readingTime || '',
            emailGateEnabled: data.emailGateEnabled || false,
            downloadEnabled: data.downloadEnabled !== false,
            watermarkEnabled: data.watermarkEnabled || false,
            expiresAt: data.expiresAt || '',
            fonts: data.fonts || {},
            textSizes: data.textSizes || {},
            companyPosition: data.companyPosition || 'bottom'
          })
          break
        default:
          // For website, spotify, youtube - no additional type data needed
          break
      }
    }
  }, [initialData, type, isEditMode])
  
  // Active tab state
  const [activeTab, setActiveTab] = useState<'content' | 'design'>(defaultTab)
  
  // Collapsible state for quick start section
  const [isQuickStartExpanded, setIsQuickStartExpanded] = useState(true)
  const [isAuthorProfileExpanded, setIsAuthorProfileExpanded] = useState(true)
  const [isMultiShopExpanded, setIsMultiShopExpanded] = useState(true)
  const [isShopLinksExpanded, setIsShopLinksExpanded] = useState(true)
  const [isYouTubeExpanded, setIsYouTubeExpanded] = useState(true)
  const [isWebsiteExpanded, setIsWebsiteExpanded] = useState(true)
  const [isLeseprobeExpanded, setIsLeseprobeExpanded] = useState(true)
  const [isSpotifyExpanded, setIsSpotifyExpanded] = useState(true)
  
  // Welcome screen animation trigger
  const [triggerWelcomeAnimation, setTriggerWelcomeAnimation] = useState(0)
  const [isAnimating, setIsAnimating] = useState(false)
  
  // QR Style configuration
  const [styleConfig, setStyleConfig] = useState<QRStyleConfig>(() => {
    // In edit mode, load styleConfig from metadata
    if (isEditMode && initialData?.metadata?.styleConfig) {
      return initialData.metadata.styleConfig
    }
    
    // Default config for new QR codes
    return {
      errorCorrectionLevel: 'Q',
      size: 512,
      dotsType: 'square',
      dotsColor: '#000000',
      cornersSquareType: 'square',
      cornersDotType: 'square',
      cornersColor: '#000000',
      backgroundColor: '#FFFFFF',
      backgroundTransparent: false,
      logoSize: 25,
      logoMargin: 6,
      hideBackgroundDots: true,
      frameStyle: 'none',
      frameColor: '#000000',
      frameSize: 16,
      frameLabelSize: 20
    }
  })
  
  // QR Code instance for preview
  const [qrCode, setQrCode] = useState<QRCodeStyling | null>(null)
  const [qrPreviewUrl, setQrPreviewUrl] = useState<string>('')
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false)
  
  // Initialize form data from initial data
  const getInitialData = () => {
    return initialData || {}
  }
  
  
  // Track if we're submitting to prevent double submissions
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Common form data
  const [commonData, setCommonData] = useState<CommonFormData>(() => {
    const data = getInitialData()
    const commonDataInitial = {
      title: data.title || '',
      description: data.description || '',
      target_url: data.target_url || '',
      pdf_url: data.pdf_url || '',
      slug: data.slug || ''
    }
    console.log('🔍 UnifiedQRForm - Initial commonData:', commonDataInitial)
    console.log('🔍 UnifiedQRForm - getInitialData returned:', data)
    console.log('🔍 UnifiedQRForm - PDF URL found:', data.pdf_url || 'NO PDF URL')
    return commonDataInitial
  })
  
  // Type-specific form data
  const [typeData, setTypeData] = useState<any>(() => {
    const data = getInitialData()
    console.log('🔍 UnifiedQRForm - Initial load, type:', type)
    console.log('🔍 UnifiedQRForm - Initial data from props:', initialData)
    console.log('🔍 UnifiedQRForm - Data after getInitialData():', data)
    console.log('🔍 UnifiedQRForm - PDF specific data:', {
      pdf_url_from_data: data.pdf_url,
      pdf_url_from_props: initialData?.pdf_url,
      description_from_data: data.description,
      metadata: data.metadata
    })
    console.log('🔍 UnifiedQRForm - isEditMode:', isEditMode)
    switch(type) {
      case 'multishop':
        console.log('🛍️ Initial MultiShop state - data:', data)
        console.log('🛍️ Initial MultiShop state - data.bookTitle:', data.bookTitle)
        console.log('🛍️ Initial MultiShop state - data.metadata?.bookTitle:', data.metadata?.bookTitle)
        return {
          bookTitle: data.bookTitle || data.metadata?.bookTitle || '',
          author: data.author || data.metadata?.author || '',
          isbn: data.isbn || data.metadata?.isbn || '',
          coverUrl: data.coverUrl || data.metadata?.coverUrl || '',
          publishDate: data.publishDate || data.metadata?.publishDate || '',
          description: data.description || data.metadata?.description || '',
          shops: data.shops || data.metadata?.shops || [{ name: '', url: '', price: '', currency: 'EUR' }],
          primaryShop: data.primaryShop || data.metadata?.primaryShop || '',
          defaultCurrency: data.defaultCurrency || data.metadata?.defaultCurrency || 'EUR',
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
          textSizes: data.textSizes || data.metadata?.textSizes || { bookTitle: 'text-2xl', author: 'text-base', shopName: 'text-base', price: 'text-sm', branding: 'text-lg' },
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
          openDirectly: data.openDirectly || data.metadata?.openDirectly || false
        }
      case 'author':
        // Check if this is a completely new form (no data entered)
        const isEmptyForm = !data.name && !data.shortBio && !data.bio && !data.fullBio && !isEditMode;
        console.log('👤 Initial Author state - data:', data)
        return {
          name: data.name || data.metadata?.name || '',
          title: data.title || data.metadata?.title || 'Autor',
          shortBio: data.shortBio || data.metadata?.shortBio || '',
          bio: data.bio || data.fullBio || data.metadata?.bio || data.metadata?.fullBio || '',
          profileImageUrl: data.profileImageUrl || data.metadata?.profileImageUrl || '',
          socialMedia: data.socialLinks || data.socialMedia || data.metadata?.socialLinks || data.metadata?.socialMedia || { website: '', instagram: '', twitter: '', facebook: '', linkedin: '', tiktok: '' },
          // Add placeholder flag to track if we should show placeholder content
          showPlaceholder: isEmptyForm,
          contact: data.contact || data.metadata?.contact || { email: '', agent: '' },
          awards: data.awards || data.metadata?.awards || [],
          pressKitUrl: data.pressKitUrl || data.metadata?.pressKitUrl || '',
          videoUrl: data.videoUrl || data.metadata?.videoUrl || '',
          newsletterEnabled: data.newsletterEnabled || data.metadata?.newsletterEnabled || false,
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
          fonts: data.fonts || data.metadata?.fonts || {},
          textSizes: data.textSizes || data.metadata?.textSizes || {},
          newAward: ''
        }
      case 'sample':
        console.log('📚 Initial BookSample state - data:', data)
        console.log('📚 Initial BookSample state - metadata:', data.metadata)
        return {
          title: data.title || data.metadata?.title || '',
          author: data.author || data.metadata?.author || '',
          pdfUrl: data.pdfUrl || data.pdf_url || data.metadata?.pdfUrl || '',
          coverUrl: data.coverUrl || data.metadata?.coverUrl || '',
          genre: data.genre || data.metadata?.genre || '',
          publicationDate: data.publicationDate || data.metadata?.publicationDate || '',
          publisher: data.publisher || data.metadata?.publisher || '',
          isbn: data.isbn || data.metadata?.isbn || '',
          samplePages: data.samplePages || data.metadata?.samplePages || '',
          readingTimeMinutes: data.readingTimeMinutes || data.metadata?.readingTimeMinutes || undefined,
          emailGateEnabled: data.emailGateEnabled || data.metadata?.emailGateEnabled || false,
          downloadEnabled: data.downloadEnabled !== false && (data.metadata?.downloadEnabled !== false),
          watermarkEnabled: data.watermarkEnabled || data.metadata?.watermarkEnabled || false,
          expiresAt: data.expiresAt || data.metadata?.expiresAt || '',
          description: data.description || data.metadata?.description || '',
          pages: data.pages || data.metadata?.pages || undefined,
          year: data.year || data.metadata?.year || '',
          language: data.language || data.metadata?.language || '',
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
          textSizes: data.textSizes || data.metadata?.textSizes || {
            branding: 'text-base',
            title: 'text-2xl',
            author: 'text-lg',
            metadata: 'text-sm',
            description: 'text-base'
          },
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
          customColors: customColors
        }
      case 'pdf':
        console.log('🔍 Initializing PDF typeData from data:', data)
        return {
          showDirectly: (data.showDirectly !== undefined ? data.showDirectly : data.metadata?.showDirectly) || false,
          openDirectly: (data.openDirectly !== undefined ? data.openDirectly : data.metadata?.openDirectly) || false,
          password: data.password || data.metadata?.password || null,
          welcomeScreen: data.welcomeScreen || data.metadata?.welcomeScreen || { enabled: false, image: '', duration: 3 },
          socialLinks: data.socialLinks || data.metadata?.socialLinks || {},
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
          buttonStyle: data.buttonStyle || data.metadata?.buttonStyle || { shape: 'pill', shadow: true, text: 'PDF herunterladen' },
          qrCodeName: data.qrCodeName || data.metadata?.qrCodeName || '',
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' }
        }
      case 'website':
        console.log('🌐 Initial Website state - data:', data)
        return {
          favicon: data.favicon || data.metadata?.favicon || '',
          buttonText: data.buttonText || data.metadata?.buttonText || 'Website besuchen',
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
          textSizes: data.textSizes || data.metadata?.textSizes || { title: 'text-2xl', description: 'text-base', url: 'text-sm' },
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom',
          openDirectly: data.openDirectly || data.metadata?.openDirectly || false
        }
      case 'spotify':
        console.log('🎵 Initial Spotify state - data:', data)
        console.log('🎵 Initial Spotify state - data.bookTitle:', data.bookTitle)
        console.log('🎵 Initial Spotify state - data.metadata?.bookTitle:', data.metadata?.bookTitle)
        return {
          bookTitle: data.bookTitle || data.metadata?.bookTitle || '',
          author: data.author || data.metadata?.author || '',
          isbn: data.isbn || data.metadata?.isbn || '',
          coverUrl: data.coverUrl || data.metadata?.coverUrl || '',
          description: data.description || data.metadata?.description || '',
          showBookCover: data.showBookCover !== false && (data.metadata?.showBookCover !== false),
          playerTheme: data.playerTheme || data.metadata?.playerTheme || 'dark',
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
          textSizes: data.textSizes || data.metadata?.textSizes || { bookTitle: 'text-2xl', description: 'text-base' },
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom'
        }
      case 'youtube':
        console.log('📺 Initial YouTube state - data:', data)
        console.log('📺 Initial YouTube state - metadata:', data.metadata)
        return {
          bookTitle: data.bookTitle || data.metadata?.bookTitle || '',
          author: data.author || data.metadata?.author || '',
          isbn: data.isbn || data.metadata?.isbn || '',
          coverUrl: data.coverUrl || data.metadata?.coverUrl || '',
          description: data.description || data.metadata?.description || '',
          autoplay: data.autoplay || data.metadata?.autoplay || false,
          fonts: data.fonts || data.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
          textSizes: data.textSizes || data.metadata?.textSizes || { bookTitle: 'text-2xl', description: 'text-base' },
          companyPosition: data.companyPosition || data.metadata?.companyPosition || 'bottom'
        }
      default:
        return {}
    }
  })
  
  // Reset type-specific data when type changes (only in create mode, not edit mode)
  useEffect(() => {
    console.log('🔍 UnifiedQRForm - Type change effect, type:', type, 'isEditMode:', isEditMode)
    
    // Don't reset data in edit mode or if we have initial data
    if (isEditMode || initialData) {
      console.log('🔍 UnifiedQRForm - Skipping reset because isEditMode or initialData exists')
      return
    }
    
    switch(type) {
      case 'multishop':
        setTypeData({
          bookTitle: '',
          author: '',
          isbn: '',
          coverUrl: '',
          publishDate: '',
          description: '',
          shops: [{ name: 'Amazon', url: '', price: '', currency: 'EUR' }],
          primaryShop: 'Amazon',
          defaultCurrency: 'EUR',
          fonts: { heading: 'Inter', body: 'Inter' },
          textSizes: { bookTitle: 'text-2xl', author: 'text-base', shopName: 'text-base', price: 'text-sm', branding: 'text-lg' },
          companyPosition: 'bottom',
          openDirectly: false
        })
        break
      case 'author':
        setTypeData({
          name: '',
          title: 'Autor',
          shortBio: '',
          bio: '',
          profileImageUrl: '',
          socialMedia: { website: '', instagram: '', twitter: '', facebook: '', linkedin: '', tiktok: '' },
          contact: { email: '', agent: '' },
          awards: [],
          pressKitUrl: '',
          videoUrl: '',
          newsletterEnabled: false,
          companyPosition: 'bottom',
          fonts: {},
          textSizes: {},
          newAward: ''
        })
        break
      case 'sample':
        setTypeData(prev => {
          // Don't reset if we're editing or have initial data
          if (isEditMode || initialData || (prev && Object.keys(prev).length)) {
            return prev;
          }
          // Only set defaults for new creation
          return {
            title: '',
            author: '',
            pdfUrl: '',
            coverUrl: '',
            genre: '',
            publicationDate: '',
            publisher: '',
            isbn: '',
            samplePages: '',
            readingTimeMinutes: undefined,
            emailGateEnabled: false,
            downloadEnabled: true,
            watermarkEnabled: false,
            expiresAt: ''
          }
        })
        break
      case 'pdf':
        // Only set default values if typeData is empty
        setTypeData(prev => {
          // If we already have data, keep it
          if (prev && Object.keys(prev).length > 0) {
            console.log('🔍 Keeping existing PDF typeData:', prev)
            return prev
          }
          // Otherwise set defaults
          console.log('🔍 Setting default PDF typeData')
          return {
            showDirectly: false,
            openDirectly: false,
            password: null,
            welcomeScreen: { enabled: false, image: '', duration: 3 },
            socialLinks: {},
            companyPosition: 'bottom',
            buttonStyle: { shape: 'pill', shadow: true, text: 'PDF herunterladen' },
            qrCodeName: '',
            fonts: { heading: 'Inter', body: 'Inter' }
          }
        })
        break
      case 'website':
        setTypeData({
          favicon: '',
          buttonText: 'Website besuchen',
          fonts: { heading: 'Inter', body: 'Inter' },
          textSizes: { title: 'text-2xl', description: 'text-base', url: 'text-sm' },
          companyPosition: 'bottom',
          openDirectly: false
        })
        break
      default:
        setTypeData({})
    }
    // Reset common data for simple types
    if (!['multishop', 'author', 'sample'].includes(type)) {
      console.log('⚠️ Type effect running - would reset commonData')
      console.log('⚠️ isEditMode:', isEditMode)
      console.log('⚠️ initialData exists:', !!initialData)
      
      // Don't reset data in edit mode or if we have initial data
      if (isEditMode || initialData) {
        console.log('🔍 UnifiedQRForm - Skipping commonData reset because isEditMode or initialData exists')
        return
      }
      
      setCommonData(prev => ({
        ...prev,
        title: '',
        description: '',
        target_url: '',
        pdf_url: ''
      }))
    }
  }, [type, isEditMode, initialData])
  
  // Add local color mode state if not controlled
  const [localColorMode, setLocalColorMode] = useState<'gradient' | 'structured'>(() => {
    // Check global persistence first (survives auth state changes)
    if (!isEditMode) {
      const persistedState = colorPersistence.getState()
      if (persistedState?.colorMode) {
        console.log('🎨 UnifiedQRForm - Using global persisted colorMode:', persistedState.colorMode)
        return persistedState.colorMode
      }
    }
    
    // Initialize from initialData if available (for both edit mode and wizard mode)
    if (initialData?.metadata?.colorMode) {
      return initialData.metadata.colorMode
    }
    // Check sessionStorage for temporary state (helps with re-renders)
    if (!isEditMode) {
      const saved = sessionStorage.getItem('qr_form_color_mode')
      if (saved && ['gradient', 'structured'].includes(saved)) {
        return saved as 'gradient' | 'structured'
      }
    }
    return 'structured'
  })
  const colorMode = propColorMode !== undefined ? propColorMode : localColorMode
  
  // Prepare preview data based on type
  const previewData = useMemo(() => {
    switch(type) {
      case 'pdf':
        return {
          title: commonData.title,
          description: commonData.description,
          pdf_url: commonData.pdf_url,
          showDirectly: typeData.showDirectly,
          openDirectly: typeData.openDirectly,
          password: typeData.password,
          welcomeScreen: typeData.welcomeScreen,
          socialLinks: typeData.socialLinks,
          companyPosition: typeData.companyPosition || 'bottom',
          buttonStyle: typeData.buttonStyle,
          qrCodeName: typeData.qrCodeName,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes, // Add text sizes to preview data
          customColors: customColors, // Add custom colors to preview data
          animationTrigger: triggerWelcomeAnimation
        }
      case 'spotify':
        return {
          ...commonData,
          ...typeData,
          customColors: customColors,
          colorMode: colorMode,
          brandingType: brandingType,
          userBranding: userBranding
        }
      case 'multishop':
        return {
          ...typeData,
          customColors: customColors,
          colorMode: colorMode
        }
      case 'author':
        // Transform for preview
        const placeholderContent = typeData.showPlaceholder ? {
          name: 'Max Mustermann',
          profession: 'Bestsellerautor',
          shortBio: 'Ein preisgekrönter Autor mit über 20 Jahren Erfahrung in der Literaturwelt. Bekannt für fesselnde Geschichten, die Herz und Verstand gleichermaßen berühren.',
          fullBio: `Max Mustermann ist ein international anerkannter Autor, dessen Werke in über 30 Sprachen übersetzt wurden.

Geboren in Berlin, entdeckte er früh seine Leidenschaft für das geschriebene Wort. Nach dem Studium der Germanistik und Philosophie veröffentlichte er seinen ersten Roman, der sofort die Bestsellerlisten stürmte.

**Wichtige Stationen:**
- 2005: Debütroman "Schattenlichter"
- 2010: Deutscher Buchpreis für "Die Stille zwischen den Worten"
- 2015: Internationale Anerkennung mit "Zeitenwende"
- 2020: Gesamtauflage überschreitet 5 Millionen Exemplare

*"Schreiben ist für mich wie Atmen - eine Notwendigkeit, keine Wahl."*

Seine Bücher zeichnen sich durch tiefgründige Charakterentwicklung und überraschende Wendungen aus. Kritiker loben besonders seine Fähigkeit, komplexe Themen zugänglich zu machen.`,
          awards: ['Deutscher Buchpreis 2010', 'Literaturpreis der Stadt München 2015', 'Goldene Feder 2020'],
          socialLinks: {
            website: 'www.maxmustermann.de',
            instagram: '@maxmustermann_autor',
            facebook: 'maxmustermannautor'
          }
        } : {};
        
        return {
          name: typeData.showPlaceholder ? placeholderContent.name : typeData.name || '',
          profession: typeData.showPlaceholder ? placeholderContent.profession : (typeData.title || 'Autor'),
          shortBio: typeData.showPlaceholder ? placeholderContent.shortBio : typeData.shortBio || '',
          fullBio: typeData.showPlaceholder ? placeholderContent.fullBio : typeData.bio || '',
          profileImageUrl: typeData.profileImageUrl,
          socialLinks: typeData.showPlaceholder ? placeholderContent.socialLinks : (typeData.socialMedia || {}),
          contact: typeData.contact,
          awards: typeData.showPlaceholder ? placeholderContent.awards : typeData.awards || [],
          pressKitUrl: typeData.pressKitUrl,
          videoUrl: typeData.videoUrl,
          newsletterEnabled: typeData.newsletterEnabled,
          companyPosition: typeData.companyPosition,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes,
          customColors: customColors
        }
      case 'sample':
        return {
          ...typeData,
          customColors: customColors
        }
      case 'website':
        return {
          title: commonData.title,
          description: commonData.description,
          target_url: commonData.target_url,
          favicon: typeData.favicon,
          buttonText: typeData.buttonText,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes,
          companyPosition: typeData.companyPosition,
          openDirectly: typeData.openDirectly,
          customColors: customColors,
          colorMode: colorMode
        }
      case 'youtube':
        return {
          title: commonData.title,
          description: typeData.description,
          target_url: commonData.target_url,
          bookTitle: typeData.bookTitle,
          author: typeData.author,
          isbn: typeData.isbn,
          coverUrl: typeData.coverUrl,
          autoplay: typeData.autoplay,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes,
          companyPosition: typeData.companyPosition,
          customColors: customColors,
          colorMode: colorMode
        }
      default:
        return commonData
    }
  }, [type, commonData, typeData, selectedBook, books, customColors, triggerWelcomeAnimation, colorMode])
  
  // Debounce preview data
  const debouncedPreviewData = useDebounce(previewData, 300)
  
  // Debounced slug for availability check
  const debouncedSlug = useDebounce(customSlug, 500)
  const setColorMode = useCallback((mode: 'gradient' | 'structured') => {
    if (!isEditMode) {
      sessionStorage.setItem('qr_form_color_mode', mode)
    }
    if (onColorModeChange) {
      onColorModeChange(mode)
    } else {
      setLocalColorMode(mode)
    }
  }, [isEditMode, onColorModeChange])
  
  // Update form data when initialData changes (for wizard navigation)
  useEffect(() => {
    if (initialData && !isEditMode) {
      console.log('🔄 UnifiedQRForm - Updating form data from initialData change:', initialData)
      
      // Update common data
      setCommonData({
        title: initialData.title || '',
        description: initialData.description || '',
        target_url: initialData.target_url || '',
        pdf_url: initialData.pdf_url || '',
        slug: initialData.slug || ''
      })
      
      // Update type-specific data based on current type
      const typeSpecificData = (() => {
        switch (type) {
          case 'spotify':
            console.log('🔍 UnifiedQRForm - Syncing spotify data from initialData:', initialData)
            return {
              bookTitle: initialData.bookTitle || initialData.metadata?.bookTitle || initialData.title || '',
              author: initialData.author || initialData.metadata?.author || '',
              isbn: initialData.isbn || initialData.metadata?.isbn || '',
              coverUrl: initialData.coverUrl || initialData.metadata?.coverUrl || '',
              description: initialData.description || initialData.metadata?.description || '',
              showBookCover: initialData.showBookCover !== false && (initialData.metadata?.showBookCover !== false),
              playerTheme: initialData.playerTheme || initialData.metadata?.playerTheme || 'dark',
              fonts: initialData.fonts || initialData.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || { bookTitle: 'text-2xl', description: 'text-base' },
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom'
            }
          case 'youtube':
            console.log('🔍 UnifiedQRForm - Syncing youtube data from initialData:', initialData)
            return {
              // Book data
              bookTitle: initialData.bookTitle || initialData.metadata?.bookTitle || '',
              author: initialData.author || initialData.metadata?.author || '',
              isbn: initialData.isbn || initialData.metadata?.isbn || '',
              coverUrl: initialData.coverUrl || initialData.metadata?.coverUrl || '',

              // YouTube data
              description: initialData.description || initialData.metadata?.description || '',
              autoplay: initialData.autoplay || initialData.metadata?.autoplay || false,

              // Typography - check metadata for fallbacks
              fonts: initialData.fonts || initialData.metadata?.fonts || {
                heading: initialData.fonts?.heading || initialData.metadata?.fonts?.heading || 'Inter',
                body: initialData.fonts?.body || initialData.metadata?.fonts?.body || 'Inter'
              },
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || {
                bookTitle: initialData.textSizes?.bookTitle || initialData.metadata?.textSizes?.bookTitle || 'text-2xl',
                description: initialData.textSizes?.description || initialData.metadata?.textSizes?.description || 'text-base',
                branding: initialData.textSizes?.branding || initialData.metadata?.textSizes?.branding || 'text-lg'
              },

              // Layout
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom'
            }
          case 'pdf':
            console.log('🔍 UnifiedQRForm - Syncing pdf data from initialData:', initialData)
            return {
              // Core PDF fields
              showDirectly: (initialData.showDirectly !== undefined ? initialData.showDirectly : initialData.metadata?.showDirectly) || false,
              openDirectly: (initialData.openDirectly !== undefined ? initialData.openDirectly : initialData.metadata?.openDirectly) || false,
              password: initialData.password || initialData.metadata?.password || null,
              welcomeScreen: initialData.welcomeScreen || initialData.metadata?.welcomeScreen || { enabled: false, image: '', duration: 3 },
              socialLinks: initialData.socialLinks || initialData.metadata?.socialLinks || {},
              buttonStyle: initialData.buttonStyle || initialData.metadata?.buttonStyle || { shape: 'pill', shadow: true, text: 'PDF herunterladen' },
              qrCodeName: initialData.qrCodeName || initialData.metadata?.qrCodeName || '',

              // Book metadata fields
              pdfUrl: initialData.pdfUrl || initialData.pdf_url || initialData.metadata?.pdfUrl || '',
              title: initialData.title || initialData.metadata?.title || '',
              author: initialData.author || initialData.metadata?.author || '',
              coverUrl: initialData.coverUrl || initialData.metadata?.coverUrl || '',
              isbn: initialData.isbn || initialData.metadata?.isbn || '',
              publicationDate: initialData.publicationDate || initialData.metadata?.publicationDate || '',
              publisher: initialData.publisher || initialData.metadata?.publisher || '',
              primaryColor: initialData.primaryColor || initialData.metadata?.primaryColor || '#3B82F6',
              autoFit: initialData.autoFit !== false && (initialData.metadata?.autoFit !== false),

              // Layout and styling
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom',
              fonts: initialData.fonts || initialData.metadata?.fonts || { heading: 'Inter', body: 'Inter' }
            }
          case 'multishop':
            console.log('🔍 UnifiedQRForm - Syncing multishop data from initialData:', initialData)
            return {
              // Critical fix: Add bookTitle field that was missing
              bookTitle: initialData.bookTitle || initialData.metadata?.bookTitle || initialData.title || '',
              title: initialData.title || '',
              description: initialData.description || initialData.metadata?.description || '',
              price: initialData.price || initialData.metadata?.price || '',
              author: initialData.author || initialData.metadata?.author || '',
              coverUrl: initialData.coverUrl || initialData.metadata?.coverUrl || '',
              shops: initialData.shops || initialData.metadata?.shops || [],
              newShop: { name: '', url: '', icon: 'shopping-cart' },
              // Add missing fields that were being lost - with metadata fallbacks
              isbn: initialData.isbn || initialData.metadata?.isbn || '',
              publishDate: initialData.publishDate || initialData.metadata?.publishDate || '',
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom',
              fonts: initialData.fonts || initialData.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || {},
              openDirectly: initialData.openDirectly || initialData.metadata?.openDirectly || false,
              // Additional fields that might be needed
              primaryShop: initialData.primaryShop || initialData.metadata?.primaryShop || '',
              defaultCurrency: initialData.defaultCurrency || initialData.metadata?.defaultCurrency || 'EUR'
            }
          case 'author':
            console.log('🔍 UnifiedQRForm - Syncing author data from initialData:', initialData)
            return {
              name: initialData.name || initialData.metadata?.name || '',
              title: initialData.title || initialData.metadata?.title || 'Autor',
              shortBio: initialData.shortBio || initialData.metadata?.shortBio || '',
              // Check multiple sources for bio
              bio: initialData.bio || initialData.fullBio || initialData.metadata?.bio || initialData.metadata?.fullBio || '',
              profileImageUrl: initialData.profileImageUrl || initialData.metadata?.profileImageUrl || '',
              coverImageUrl: initialData.coverImageUrl || initialData.metadata?.coverImageUrl || '',
              website: initialData.website || initialData.metadata?.website || '',
              // Check both socialMedia and socialLinks
              socialMedia: initialData.socialMedia || initialData.socialLinks || initialData.metadata?.socialMedia || initialData.metadata?.socialLinks || {},
              awards: initialData.awards || initialData.metadata?.awards || [],
              // Use contact object structure to match initial state
              contact: initialData.contact || {
                email: initialData.contactEmail || initialData.metadata?.contactEmail || '',
                agent: initialData.agentContact || initialData.metadata?.agentContact || ''
              },
              // Remove separate contactEmail/agentContact fields - now using contact object
              pressKitUrl: initialData.pressKitUrl || initialData.metadata?.pressKitUrl || '',
              videoUrl: initialData.videoUrl || initialData.metadata?.videoUrl || '',
              usePlaceholder: initialData.usePlaceholder !== false,
              newsletterEnabled: initialData.newsletterEnabled || initialData.metadata?.newsletterEnabled || false,
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom',
              fonts: initialData.fonts || initialData.metadata?.fonts || {},
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || {},
              newAward: '',
              // Add showPlaceholder to match initial state
              showPlaceholder: initialData.showPlaceholder || false
            }
          case 'sample':
            console.log('🔍 UnifiedQRForm - Syncing sample data from initialData:', initialData)
            return {
              title: initialData.title || initialData.metadata?.title || '',
              author: initialData.author || initialData.metadata?.author || '',
              pdfUrl: initialData.pdfUrl || initialData.pdf_url || initialData.metadata?.pdfUrl || '',
              coverUrl: initialData.coverUrl || initialData.metadata?.coverUrl || '',
              genre: initialData.genre || initialData.metadata?.genre || '',
              publicationDate: initialData.publicationDate || initialData.metadata?.publicationDate || '',
              publisher: initialData.publisher || initialData.metadata?.publisher || '',
              isbn: initialData.isbn || initialData.metadata?.isbn || '',
              samplePages: initialData.samplePages || initialData.metadata?.samplePages || '',
              readingTimeMinutes: initialData.readingTimeMinutes || initialData.metadata?.readingTimeMinutes || undefined,
              emailGateEnabled: initialData.emailGateEnabled || initialData.metadata?.emailGateEnabled || false,
              downloadEnabled: initialData.downloadEnabled !== false && (initialData.metadata?.downloadEnabled !== false),
              watermarkEnabled: initialData.watermarkEnabled || initialData.metadata?.watermarkEnabled || false,
              expiresAt: initialData.expiresAt || initialData.metadata?.expiresAt || '',
              // Add missing fields with metadata fallbacks
              description: initialData.description || initialData.metadata?.description || '',
              pages: initialData.pages || initialData.metadata?.pages || undefined,
              year: initialData.year || initialData.metadata?.year || '',
              language: initialData.language || initialData.metadata?.language || '',
              fonts: initialData.fonts || initialData.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || {},
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom'
            }
          case 'website':
            console.log('🔍 UnifiedQRForm - Syncing website data from initialData:', initialData)
            return {
              favicon: initialData.favicon || initialData.metadata?.favicon || '',
              buttonText: initialData.buttonText || initialData.metadata?.buttonText || 'Website besuchen',
              fonts: initialData.fonts || initialData.metadata?.fonts || { heading: 'Inter', body: 'Inter' },
              textSizes: initialData.textSizes || initialData.metadata?.textSizes || { title: 'text-2xl', description: 'text-base', url: 'text-sm' },
              companyPosition: initialData.companyPosition || initialData.metadata?.companyPosition || 'bottom',
              openDirectly: initialData.openDirectly || initialData.metadata?.openDirectly || false
            }
          default:
            return {}
        }
      })()
      
      setTypeData(typeSpecificData)
    }
  }, [initialData, type, isEditMode])
  
  // Helper function to update type data and trigger save
  const handleTypeDataChange = useCallback((updates: any) => {
    console.log('🔍 UnifiedQRForm - handleTypeDataChange called with:', updates);
    setTypeData(prevData => {
      const mergedData = { ...prevData, ...updates };
      
      // Check if user has any actual content (not empty strings)
      const hasContent = 
        (mergedData.name && mergedData.name.trim() !== '') ||
        (mergedData.shortBio && mergedData.shortBio.trim() !== '') ||
        (mergedData.bio && mergedData.bio.trim() !== '') ||
        (mergedData.title && mergedData.title.trim() !== '' && mergedData.title !== 'Autor') ||
        (mergedData.awards && mergedData.awards.length > 0) ||
        (mergedData.socialMedia && Object.values(mergedData.socialMedia).some((v: any) => v && v.trim() !== ''));
      
      // Turn off placeholder if user has any content
      mergedData.showPlaceholder = hasContent ? false : (prevData.showPlaceholder !== false);
      
      console.log('🔍 UnifiedQRForm - typeData after merge:', mergedData);
      console.log('🔍 UnifiedQRForm - hasContent:', hasContent, 'showPlaceholder:', mergedData.showPlaceholder);
      return mergedData;
    });
  }, []);
  
  // Helper function to update common data and trigger save
  const handleCommonDataChange = useCallback((updates: any) => {
    console.log('📝 CommonData changing to:', updates);
    console.log('📝 PDF URL changing from', commonData.pdf_url, 'to', updates.pdf_url);
    setCommonData(updates);
  }, [commonData.pdf_url]);
  
  
  
  // Memoize preview component
  const previewComponent = useMemo(() => {
    console.log('🎨 Creating preview with customColors:', customColors)
    return (
      <UnifiedPreview
        type={type}
        data={debouncedPreviewData}
        brandingType={brandingType}
        customBranding={brandingType === 'custom' && userBranding ? {
          companyName: userBranding.company_name,
          logoUrl: userBranding.logo_url,
          primaryColor: customColors.primaryColor,
          secondaryColor: customColors.secondaryColor,
          textColor: customColors.textColor,
          wrapperColor: customColors.wrapperColor,
          wrapperOpacity: customColors.wrapperOpacity,
          displayType: userBranding.display_type
        } : undefined}
        colorMode={colorMode}
      />
    )
  }, [type, debouncedPreviewData, brandingType, userBranding, customColors, colorMode])
  
  // Generate QR preview data
  const qrData = useMemo(() => {
    // If editing, use the actual redirect URL
    if (isEditMode && editingLink?.redirect) {
      return editingLink.redirect;
    }
    
    // Otherwise generate preview URL
    const baseUrl = import.meta.env.VITE_QR_DOMAIN || 'https://beyondpages.io'
    const slug = customSlug || editingLink?.slug || 'preview'
    return currentAccountSlug 
      ? `${baseUrl}/qr/${currentAccountSlug}/${slug}`
      : `${baseUrl}/r/${slug}`
  }, [customSlug, currentAccountSlug, isEditMode, editingLink])

  // Initialize QR preview on mount and update when style config changes
  useEffect(() => {
    let currentUrl: string | null = null

    console.log('QR Preview Effect - activeTab:', activeTab)
    
    // Generate QR code preview
    if (qrData && styleConfig) {
      console.log('Generating QR code with data:', qrData)
      console.log('Style config:', styleConfig)
      
      try {
        const qrConfig: any = {
          width: styleConfig.size || 512,
          height: styleConfig.size || 512,
          data: qrData,
          qrOptions: {
            errorCorrectionLevel: styleConfig.errorCorrectionLevel || 'Q'
          },
          dotsOptions: {
            type: styleConfig.dotsType || 'square',
            color: styleConfig.dotsColor || '#000000'
          },
          cornersSquareOptions: {
            type: styleConfig.cornersSquareType || 'square',
            color: styleConfig.cornersColor || '#000000'
          },
          cornersDotOptions: {
            type: styleConfig.cornersDotType || 'square',
            color: styleConfig.cornersColor || '#000000'
          },
          backgroundOptions: {
            color: styleConfig.backgroundTransparent ? 'transparent' : (styleConfig.backgroundColor || '#FFFFFF')
          }
        }

        // Only add gradient if it exists
        if (styleConfig.dotsGradient) {
          qrConfig.dotsOptions.gradient = styleConfig.dotsGradient
        }

        // Add image configuration for the helper
        if (styleConfig.logoUrl && styleConfig.logoUrl.trim() !== '') {
          qrConfig.image = styleConfig.logoUrl;
          qrConfig.imageOptions = {
            crossOrigin: 'anonymous',
            margin: styleConfig.logoMargin || 0,
            imageSize: (styleConfig.logoSize || 25) / 100,
            hideBackgroundDots: styleConfig.hideBackgroundDots !== false
          };
        }

        console.log('Generating QR preview...')
        
        // Use the helper to generate QR code safely
        const generateQRPreview = async () => {
          try {
            const result = await generateQRCode(qrConfig);
            
            if (result.blob) {
              currentUrl = URL.createObjectURL(result.blob);
              console.log('QR preview URL created:', currentUrl);
              setQrPreviewUrl(currentUrl);
              
              // Create a basic QR instance without problematic URLs for other uses
              const basicConfig = { ...qrConfig };
              delete basicConfig.image;
              delete basicConfig.imageOptions;
              const qr = new QRCodeStyling(basicConfig);
              setQrCode(qr);
            } else {
              console.error('Failed to generate QR code:', result.error);
              
              // Create basic QR without image
              const basicConfig = { ...qrConfig };
              delete basicConfig.image;
              delete basicConfig.imageOptions;
              const qr = new QRCodeStyling(basicConfig);
              setQrCode(qr);
            }
          } catch (error) {
            console.error('Error in QR generation process:', error);
          }
        };
        
        // Generate immediately
        generateQRPreview();
      
      } catch (error) {
        console.error('Error creating QR code:', error)
        // Don't crash the app, just continue without preview
      }
    }

    // Cleanup function
    return () => {
      if (currentUrl) {
        URL.revokeObjectURL(currentUrl)
      }
    }
  }, [styleConfig, qrData, activeTab])

  // Check slug availability
  useEffect(() => {
    if (!debouncedSlug || debouncedSlug.length < 3) {
      setSlugAvailable(null)
      return
    }

    const checkSlug = async () => {
      setCheckingSlug(true)
      try {
        const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'
        const response = await fetch(`${API_BASE}/api/links/check-slug/${debouncedSlug}`, {
          headers: {
            'Authorization': `Bearer ${session?.access_token}`
          }
        })
        
        if (response.ok) {
          const { available } = await response.json()
          setSlugAvailable(available)
        } else {
          // If the endpoint doesn't exist, we'll skip the check
          setSlugAvailable(null)
        }
      } catch (error) {
        console.error('Error checking slug:', error)
        setSlugAvailable(null)
      } finally {
        setCheckingSlug(false)
      }
    }

    checkSlug()
  }, [debouncedSlug, session?.access_token])

  const handleSubmit = async (e?: React.FormEvent) => {
    console.log('🚨 handleSubmit called!');
    console.log('🚨 Event:', e);
    console.log('🚨 Event type:', e?.type);
    console.log('🚨 Event target:', e?.target);
    console.log('🚨 Current form data:', {
      commonData,
      typeData,
      type,
      isEditMode
    });
    console.log('🚨 Stack trace:', new Error().stack);
    
    if (e) e.preventDefault()
    
    // Prevent double submission
    if (isSubmitting) {
      console.log('⚠️ Already submitting, ignoring duplicate request')
      return
    }
    
    setIsSubmitting(true)
    
    // Validate author type: keine Pflichtfelder
    
    // Validate multishop type
    if (type === 'multishop') {
      if (!typeData.bookTitle || !typeData.bookTitle.trim()) {
        alert('Bitte geben Sie einen Buchtitel ein');
        setIsSubmitting(false);
        return;
      }
      if (!typeData.author || !typeData.author.trim()) {
        alert('Bitte geben Sie einen Autor ein');
        setIsSubmitting(false);
        return;
      }
      if (!typeData.shops || typeData.shops.length === 0 || !typeData.shops.some((shop: any) => shop.name && shop.url)) {
        alert('Bitte fügen Sie mindestens einen Shop mit Name und URL hinzu');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Validate sample type
    if (type === 'sample') {
      if (!typeData.title || !typeData.title.trim()) {
        alert('Bitte geben Sie einen Buchtitel ein');
        setIsSubmitting(false);
        return;
      }
      if (!typeData.author || !typeData.author.trim()) {
        alert('Bitte geben Sie einen Autor ein');
        setIsSubmitting(false);
        return;
      }
      if (!typeData.pdfUrl || !typeData.pdfUrl.trim()) {
        alert('Bitte laden Sie eine PDF-Leseprobe hoch');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Prepare submission data based on type
    let submitData: any = {}
    
    switch(type) {
      case 'pdf':
        console.log('🚨 PDF Submission - typeData state:', typeData)
        console.log('🚨 PDF Submission - showDirectly:', typeData.showDirectly)
        console.log('🚨 PDF Submission - welcomeScreen:', typeData.welcomeScreen)
        console.log('🚨 PDF Submission - socialLinks:', typeData.socialLinks)
        
        submitData = {
          title: commonData.title,
          description: commonData.description,
          target_url: commonData.pdf_url,
          pdf_url: commonData.pdf_url,
          colorMode: colorMode,
          // Include all typeData fields at root level for wizard to pass between steps
          ...typeData,
          showDirectly: true, // Always true for PDF type
          metadata: {
            description: commonData.description, // Also include in metadata for PDFProductionWrapper
            showDirectly: true, // Always show PDF button
            openDirectly: typeData.openDirectly,
            password: typeData.password,
            welcomeScreen: typeData.welcomeScreen,
            socialLinks: typeData.socialLinks,
            companyPosition: typeData.companyPosition || 'bottom',
            buttonStyle: typeData.buttonStyle,
            qrCodeName: typeData.qrCodeName,
            fonts: typeData.fonts,
            colorMode: colorMode // Include colorMode in metadata too
          }
        }
        
        console.log('🚨 PDF Submission - Final submitData:', submitData)
        break
      case 'multishop':
        // Format multishop data properly
        console.log('🛍️ Multishop submission - typeData:', typeData)
        // Ensure shop logos and local flags are included
        const shopsWithLogos = (typeData.shops || []).map((shop: any) => ({
          ...shop,
          logo: shop.logo || getShopLogo(shop.name) || undefined,
          isLocal: shop.isLocal || isLocalShop(shop.name) || undefined
        }))
        
        submitData = {
          title: typeData.bookTitle || commonData.title || 'Multi-Shop',  // Use bookTitle first for MultiShop
          description: typeData.description || commonData.description || '',
          target_url: '', // Will be set by backend
          // Include all typeData fields at root level for wizard to pass between steps
          ...typeData,
          shops: shopsWithLogos,
          colorMode: colorMode,
          metadata: {
            bookTitle: typeData.bookTitle,
            author: typeData.author,
            isbn: typeData.isbn,
            coverUrl: typeData.coverUrl,
            publishDate: typeData.publishDate,
            description: typeData.description,
            shops: shopsWithLogos,
            primaryShop: typeData.primaryShop,
            defaultCurrency: typeData.defaultCurrency || 'EUR',
            fonts: typeData.fonts || { heading: 'Inter', body: 'Inter' },
            textSizes: typeData.textSizes || {
              bookTitle: 'text-2xl',
              author: 'text-base',
              shopName: 'text-base',
              price: 'text-sm',
              branding: 'text-lg'
            },
            companyPosition: typeData.companyPosition || 'bottom',
            customColors: customColors,
            colorMode: colorMode,
            openDirectly: typeData.openDirectly || false
          }
        }
        console.log('🛍️ Multishop submission - submitData:', submitData)
        break
      case 'author':
        // Transform author data to match the expected format
        const socialLinks: any = {}
        if (typeData.socialMedia?.website) socialLinks.website = typeData.socialMedia.website
        if (typeData.socialMedia?.instagram) socialLinks.instagram = typeData.socialMedia.instagram
        if (typeData.socialMedia?.facebook) socialLinks.facebook = typeData.socialMedia.facebook
        if (typeData.socialMedia?.twitter) socialLinks.twitter = typeData.socialMedia.twitter
        if (typeData.socialMedia?.linkedin) socialLinks.linkedin = typeData.socialMedia.linkedin
        if (typeData.socialMedia?.tiktok) socialLinks.tiktok = typeData.socialMedia.tiktok
        
        submitData = {
          name: typeData.name,
          shortBio: typeData.shortBio,
          fullBio: typeData.bio,
          profileImageUrl: typeData.profileImageUrl,
          socialLinks: Object.keys(socialLinks).length > 0 ? socialLinks : undefined,
          contact: typeData.contact || {},
          awards: typeData.awards || [],
          pressKitUrl: typeData.pressKitUrl,
          videoUrl: typeData.videoUrl,
          newsletterEnabled: typeData.newsletterEnabled || false,
          companyPosition: typeData.companyPosition || 'bottom',
          fonts: typeData.fonts || {},
          textSizes: typeData.textSizes || {},
          customColors: customColors,
          colorMode: colorMode,
          // Include all typeData fields at root level for wizard to pass between steps
          ...typeData,
          // But override with the properly formatted fields above
          bio: typeData.bio, // Keep the original bio field
          socialMedia: typeData.socialMedia // Keep the original socialMedia structure
        }
        break
      case 'sample':
        // Log the current state
        console.log('📚 Sample submission - typeData:', typeData)
        console.log('📚 Sample submission - commonData:', commonData)
        
        submitData = {
          ...typeData,
          title: typeData.title || commonData.title || '',  // Ensure title is included
          description: typeData.description || commonData.description || '',
          target_url: typeData.pdfUrl || '',  // Sample links redirect to PDF
          pdf_url: typeData.pdfUrl || '',
          type: 'sample',  // Explicitly set the type
          customColors: customColors,
          colorMode: colorMode,
          metadata: {
            ...typeData.metadata,
            title: typeData.title,
            author: typeData.author,
            pdfUrl: typeData.pdfUrl,
            coverUrl: typeData.coverUrl,
            genre: typeData.genre,
            publicationDate: typeData.publicationDate,
            publisher: typeData.publisher,
            isbn: typeData.isbn,
            pages: typeData.pages,
            emailGateEnabled: typeData.emailGateEnabled,
            downloadEnabled: typeData.downloadEnabled,
            customColors: customColors,
            colorMode: colorMode,
            fonts: typeData.fonts,
            textSizes: typeData.textSizes,
            companyPosition: typeData.companyPosition
          }
        }
        
        console.log('📚 Sample submission - submitData:', submitData)
        break
      case 'website':
        submitData = {
          title: commonData.title,
          description: commonData.description,
          target_url: commonData.target_url,
          favicon: typeData.favicon,
          buttonText: typeData.buttonText,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes,
          companyPosition: typeData.companyPosition,
          openDirectly: typeData.openDirectly,
          customColors: customColors,
          colorMode: colorMode,
          metadata: {
            favicon: typeData.favicon,
            buttonText: typeData.buttonText,
            fonts: typeData.fonts,
            textSizes: typeData.textSizes,
            companyPosition: typeData.companyPosition,
            openDirectly: typeData.openDirectly,
            customColors: customColors,
            colorMode: colorMode
          }
        }
        break
      case 'spotify':
        submitData = {
          title: typeData.bookTitle || 'Spotify Playlist',
          description: typeData.description || '',
          target_url: commonData.target_url,
          bookTitle: typeData.bookTitle,
          author: typeData.author,
          isbn: typeData.isbn,
          coverUrl: typeData.coverUrl,
          showBookCover: typeData.showBookCover,
          fonts: typeData.fonts,
          textSizes: typeData.textSizes,
          companyPosition: typeData.companyPosition,
          customColors: customColors,
          colorMode: colorMode,
          metadata: {
            bookTitle: typeData.bookTitle,
            author: typeData.author,
            isbn: typeData.isbn,
            coverUrl: typeData.coverUrl,
            showBookCover: typeData.showBookCover,
            description: typeData.description,
            fonts: typeData.fonts,
            textSizes: typeData.textSizes,
            companyPosition: typeData.companyPosition,
            customColors: customColors,
            colorMode: colorMode
          }
        }
        break
      case 'youtube':
        submitData = {
          title: commonData.title || 'YouTube Video',
          description: typeData.description || '',
          target_url: commonData.target_url,
          metadata: {
            bookTitle: typeData.bookTitle,
            author: typeData.author,
            isbn: typeData.isbn,
            coverUrl: typeData.coverUrl,
            description: typeData.description,
            autoplay: typeData.autoplay,
            fonts: typeData.fonts,
            textSizes: typeData.textSizes,
            companyPosition: typeData.companyPosition,
            customColors: customColors,
            colorMode: colorMode
          }
        }
        break
      default:
        submitData = {
          title: commonData.title,
          description: commonData.description,
          target_url: commonData.target_url,
          pdf_url: commonData.pdf_url
        }
    }
    
    console.log('🚀 Submitting data:', submitData)
    console.log('📊 Submit data size:', JSON.stringify(submitData).length, 'bytes')
    console.log('🎨 Style config:', styleConfig)
    console.log('📏 Style config size:', JSON.stringify(styleConfig).length, 'bytes')
    console.log('🎨 Full metadata with styleConfig:', submitData.metadata)
    
    // Log PDF-specific fields for debugging
    if (type === 'pdf') {
      console.log('📄 PDF Fields:', {
        showDirectly: submitData.metadata?.showDirectly,
        openDirectly: submitData.metadata?.openDirectly,
        welcomeScreen: submitData.metadata?.welcomeScreen,
        socialLinks: submitData.metadata?.socialLinks
      });
    }
    
    // Add style config to metadata
    // Clean up styleConfig to avoid issues with certain URLs
    const cleanStyleConfig = { ...styleConfig }
    
    // Keep data URLs but not blob URLs
    if (cleanStyleConfig.logoUrl && cleanStyleConfig.logoUrl.startsWith('blob:')) {
      // Remove blob URLs as they can't be saved to database
      delete cleanStyleConfig.logoUrl
    }
    
    // Merge styleConfig with existing metadata
    if (!submitData.metadata) submitData.metadata = {}
    submitData.metadata = {
      ...submitData.metadata,
      styleConfig: cleanStyleConfig,
      brandingType,
      colorMode,
      ...(brandingType === 'custom' ? { customColors } : {})
    }
    
    // Generate a unique slug if none provided
    const finalSlug = customSlug || `qr-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
    
    console.log('🎯 UnifiedQRForm - About to call onSubmit with submitData:', submitData)
    console.log('🎯 UnifiedQRForm - submitData.bookTitle:', submitData.bookTitle)
    console.log('🎯 UnifiedQRForm - submitData.title:', submitData.title)
    console.log('🎯 UnifiedQRForm - submitData.metadata?.bookTitle:', submitData.metadata?.bookTitle)

    try {
      // Call the onSubmit function
      await onSubmit(submitData, localSelectedBookId || undefined, localSelectedCampaignId || undefined, finalSlug)
      
      // Clear sessionStorage on successful submission
      if (!isEditMode) {
        sessionStorage.removeItem('qr_form_branding_type')
        sessionStorage.removeItem('qr_form_custom_colors')
        sessionStorage.removeItem('qr_form_color_mode')
      }
    } catch (error) {
      // Error is handled by the parent component
      console.error('Form submission error:', error)
    } finally {
      // Always reset submission state
      setIsSubmitting(false)
    }
  }
  
  // File upload handler
  const handleFileUpload = async (file: File, uploadType: 'pdf' | 'image') => {
    if (!session?.access_token) return
    
    setUploadingFile(true)
    const formData = new FormData()
    // Backend expects 'pdf' for PDF files and 'file' for images
    formData.append(uploadType === 'pdf' ? 'pdf' : 'file', file)
    
    try {
      const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:3002'
      const response = await fetch(`${API_BASE}/api/upload/${uploadType}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        },
        body: formData
      })
      
      if (response.ok) {
        const { url } = await response.json()
        return url
      } else {
        const error = await response.json()
        console.error('Upload failed:', error)
        alert(error.error || 'Upload fehlgeschlagen')
      }
    } catch (error) {
      console.error('Upload error:', error)
      alert('Upload fehlgeschlagen. Bitte versuchen Sie es erneut.')
    } finally {
      setUploadingFile(false)
    }
  }

  // Top-level Zuordnung section shown for all types
  const supportsCover = ['multishop', 'spotify', 'youtube', 'sample'].includes(type)
  const assignmentTop = (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-6 py-4 bg-white">
        <h3 className="text-base font-semibold text-gray-900">Zuordnung</h3>
      </div>
      <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
        <div className="pt-4 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Buch</label>
              <select
                value={localSelectedBookId || ''}
                onChange={(e) => {
                  const val = e.target.value
                  setLocalSelectedBookId(val)
                  const book = books.find(b => b.id === val)
                  if (book) {
                    setSelectedBook(book)
                    // Apply book details to current type
                    const updates: any = {}
                    if (supportsCover && book.cover_url) updates.coverUrl = book.cover_url
                    if (type === 'multishop') {
                      updates.bookTitle = updates.bookTitle || book.title
                      updates.author = updates.author || (book as any).author_name
                      updates.isbn = updates.isbn || (book as any).isbn
                    } else if (type === 'sample') {
                      updates.title = updates.title || book.title
                      updates.author = updates.author || (book as any).author_name
                      updates.isbn = updates.isbn || (book as any).isbn
                    } else if (type === 'spotify' || type === 'youtube') {
                      updates.bookTitle = updates.bookTitle || book.title
                      updates.author = updates.author || (book as any).author_name
                      updates.isbn = updates.isbn || (book as any).isbn
                    }
                    if (Object.keys(updates).length) handleTypeDataChange({ ...typeData, ...updates })
                  } else {
                    setSelectedBook(null)
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Kein Buch zugeordnet</option>
                {books.map(book => (
                  <option key={book.id} value={book.id}>
                    {book.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Kampagne</label>
              <select
                value={localSelectedCampaignId || ''}
                onChange={(e) => setLocalSelectedCampaignId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
              >
                <option value="">Keine Kampagne zugeordnet</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {(campaign as any).name || campaign.title}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {supportsCover && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Cover</label>
                <div className="flex items-center gap-3">
                  {typeData.coverUrl ? (
                    <img src={typeData.coverUrl} alt="Cover" className="w-12 h-16 object-cover rounded border" />
                  ) : (
                    <div className="w-12 h-16 bg-gray-200 rounded border" />
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedBook) {
                        const url = (selectedBook as any).cover_url
                        if (url) handleTypeDataChange({ ...typeData, coverUrl: url })
                      }
                    }}
                    className="px-3 py-2 bg-blue-100 text-blue-700 rounded hover:bg-blue-200 text-sm"
                  >
                    Cover aus Buch übernehmen
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Neues Cover hochladen</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      const url = await handleFileUpload(file, 'image')
                      if (url) handleTypeDataChange({ ...typeData, coverUrl: url })
                    }
                  }}
                  className="block w-full text-sm text-gray-700"
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
  
  // Render common fields
  const renderCommonFields = () => (
    <>
      {/* Title field for most types - exclude pdf, website, youtube, spotify since they have their own sections */}
      {!['multishop', 'author', 'sample', 'pdf', 'website', 'youtube', 'spotify'].includes(type) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Titel
          </label>
          <input
            type="text"
            value={commonData.title}
            onChange={(e) => handleCommonDataChange({ ...commonData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            data-testid="qr-common-title"
          />
        </div>
      )}
      
      {/* Description for simple types - exclude pdf, website, youtube, spotify since they have their own sections */}
      {!['multishop', 'author', 'sample', 'pdf', 'website', 'youtube', 'spotify'].includes(type) && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Beschreibung
          </label>
          <textarea
            value={commonData.description}
            onChange={(e) => handleCommonDataChange({ ...commonData, description: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows={3}
            data-testid="qr-common-description"
          />
        </div>
      )}
    </>
  )
  
  // Render type-specific fields
  const renderTypeSpecificFields = () => {
    switch(type) {
      case 'pdf':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: PDF (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsQuickStartExpanded(!isQuickStartExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900">PDF</h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isQuickStartExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isQuickStartExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
                  {/* PDF Upload */}
                  <div>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 hover:border-blue-400 transition-colors duration-200">
                      {console.log('🎯 PDF Field Render - commonData.pdf_url:', commonData.pdf_url)}
                      {console.log('🎯 PDF Field Render - commonData:', commonData)}
                      {commonData.pdf_url ? (
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-green-800">PDF hochgeladen</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleCommonDataChange({ ...commonData, pdf_url: '' })}
                            className="text-sm text-red-600 hover:text-red-700 font-medium"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="file"
                            accept=".pdf"
                            id="pdf-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                const url = await handleFileUpload(file, 'pdf')
                                if (url) {
                                  handleCommonDataChange({ ...commonData, pdf_url: url })
                                }
                              }
                            }}
                            className="hidden"
                            disabled={uploadingFile}
                          />
                          <label
                            htmlFor="pdf-upload"
                            className="cursor-pointer flex flex-col items-center py-2"
                          >
                            <Upload className="w-6 h-6 text-gray-400 mb-2" />
                            <p className="text-sm text-gray-600">Klicken oder ziehen Sie eine PDF-Datei hierher <span className="text-red-500">*</span></p>
                          </label>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {/* Direct Open Toggle - moved here right after PDF upload */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className={`text-sm font-medium ${!commonData.pdf_url ? 'text-gray-400' : 'text-gray-700'}`}>PDF direkt öffnen</span>
                        <span className="text-xs text-gray-500 ml-2">(ohne gestaltete Seite)</span>
                        {!commonData.pdf_url && (
                          <span className="text-xs text-gray-400 ml-2">(PDF Upload erforderlich)</span>
                        )}
                      </div>
                      <label className={`relative inline-flex items-center ${!commonData.pdf_url ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}>
                        <input
                          type="checkbox"
                          checked={typeData.openDirectly || false}
                          onChange={(e) => {
                            if (commonData.pdf_url) {
                              console.log('🔄 PDF openDirectly toggled to:', e.target.checked);
                              handleTypeDataChange({ ...typeData, openDirectly: e.target.checked })
                            }
                          }}
                          disabled={!commonData.pdf_url}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 peer-disabled:opacity-50 peer-disabled:cursor-not-allowed"></div>
                      </label>
                    </div>
                  </div>
                  
                  {/* PDF Button Text - shown when PDF is uploaded and not opening directly */}
                  {commonData.pdf_url && !typeData.openDirectly && (
                    <div>
                      <label className="text-sm font-medium text-gray-700 block mb-2">
                        PDF-Button Text
                      </label>
                      <input
                        type="text"
                        value={typeData.buttonStyle?.text || 'PDF anzeigen'}
                        onChange={(e) => handleTypeDataChange({
                          ...typeData,
                          buttonStyle: { ...(typeData.buttonStyle || {}), text: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Text für den Button"
                      />
                    </div>
                  )}
                  
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Titel
                    </label>
                    <input
                      type="text"
                      value={commonData.title}
                      onChange={(e) => handleCommonDataChange({ ...commonData, title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Geben Sie einen Titel ein"
                      data-testid="qr-pdf-title"
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Beschreibung
                    </label>
                    <textarea
                      value={commonData.description}
                      onChange={(e) => handleCommonDataChange({ ...commonData, description: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      rows={2}
                      placeholder="Kurze Beschreibung (optional)"
                      data-testid="qr-pdf-description"
                    />
                  </div>

                  {/* Font Selection - only for PDF type */}
                  {type === 'pdf' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Schriftart
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Buchtitel-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.heading || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Fließtext-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.body || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), body: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                      </div>
                      
                      {/* Text Size Selection */}
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Textgröße
                        </label>
                        <div className="grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Branding
                            </label>
                            <select
                              value={typeData.textSizes?.branding || 'text-lg'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <option value="text-sm">Klein</option>
                              <option value="text-base">Mittel</option>
                              <option value="text-lg">Groß</option>
                              <option value="text-xl">Sehr groß</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Titel
                            </label>
                            <select
                              value={typeData.textSizes?.title || 'text-2xl'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                textSizes: { ...(typeData.textSizes || {}), title: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <option value="text-lg">Klein</option>
                              <option value="text-xl">Mittel</option>
                              <option value="text-2xl">Groß</option>
                              <option value="text-3xl">Sehr groß</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Beschreibung
                            </label>
                            <select
                              value={typeData.textSizes?.description || 'text-base'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                textSizes: { ...(typeData.textSizes || {}), description: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <option value="text-xs">Sehr klein</option>
                              <option value="text-sm">Klein</option>
                              <option value="text-base">Mittel</option>
                              <option value="text-lg">Groß</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>

            {/* Section 2: Farbe (Collapsed by default) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between list-none">
                  <span className="text-base font-medium text-gray-900">Farbe</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4">
                    {/* Full Color Customization */}
                    <div>
                      <ColorSelector
                        primaryColor={customColors.primaryColor}
                        secondaryColor={customColors.secondaryColor}
                        textColor={customColors.textColor}
                        wrapperColor={customColors.wrapperColor}
                        wrapperOpacity={customColors.wrapperOpacity}
                        colorMode={colorMode}
                        onColorModeChange={setColorMode}
                        onAllColorsChange={(colors) => {
                          if (onCustomColorsChange) {
                            onCustomColorsChange(colors)
                          } else {
                            setLocalCustomColors(colors)
                          }
                        }}
                        onPrimaryColorChange={(color) => {
                          console.log('🎨 UnifiedQRForm - Primary color change:', color)
                          console.log('🎨 UnifiedQRForm - Has onCustomColorsChange?', !!onCustomColorsChange)
                          if (onCustomColorsChange) {
                            console.log('🎨 UnifiedQRForm - Calling onCustomColorsChange')
                            onCustomColorsChange({ ...customColors, primaryColor: color })
                          } else {
                            console.log('🎨 UnifiedQRForm - Using local state')
                            setLocalCustomColors(prev => ({ ...prev, primaryColor: color }))
                          }
                        }}
                        onSecondaryColorChange={(color) => {
                          if (onCustomColorsChange) {
                            onCustomColorsChange({ ...customColors, secondaryColor: color })
                          } else {
                            setLocalCustomColors(prev => ({ ...prev, secondaryColor: color }))
                          }
                        }}
                        onTextColorChange={(color) => {
                          if (onCustomColorsChange) {
                            onCustomColorsChange({ ...customColors, textColor: color })
                          } else {
                            setLocalCustomColors(prev => ({ ...prev, textColor: color }))
                          }
                        }}
                        onWrapperColorChange={(color) => {
                          if (onCustomColorsChange) {
                            onCustomColorsChange({ ...customColors, wrapperColor: color })
                          } else {
                            setLocalCustomColors(prev => ({ ...prev, wrapperColor: color }))
                          }
                        }}
                        onWrapperOpacityChange={(opacity) => {
                          if (onCustomColorsChange) {
                            onCustomColorsChange({ ...customColors, wrapperOpacity: opacity })
                          } else {
                            setLocalCustomColors(prev => ({ ...prev, wrapperOpacity: opacity }))
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>
              </details>


            {/* Section 4: Branding (Collapsed) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  {/* Branding Settings */}
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {/* Company Position for custom branding */}
                  {brandingType === 'custom' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                      <select
                        value={typeData.companyPosition || 'bottom'}
                        onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      >
                        <option value="top">Oben</option>
                        <option value="bottom">Unten</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>
            </details>

            {/* Section 5: Soziale Netzwerke (Collapsed) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Soziale Netzwerke</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      { key: 'website', icon: Globe, name: 'Website' },
                      { key: 'instagram', icon: Instagram, name: 'Instagram' },
                      { key: 'facebook', icon: Facebook, name: 'Facebook' },
                      { key: 'twitter', icon: XIcon, name: 'X' },
                      { key: 'linkedin', icon: Linkedin, name: 'LinkedIn' },
                      { key: 'youtube', icon: Youtube, name: 'YouTube' }
                    ].map((platform) => (
                      <div key={platform.key} className="flex items-center space-x-3">
                        <platform.icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <input
                          type="url"
                          placeholder={`${platform.name} URL`}
                          value={typeData.socialLinks?.[platform.key] || ''}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            socialLinks: { ...(typeData.socialLinks || {}), [platform.key]: e.target.value }
                          })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </details>

            {/* Section 7: Erweiterte Einstellungen (Collapsed) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Erweiterte Einstellungen</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  {/* Welcome Screen Toggle */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700">Willkommensbildschirm</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={typeData.welcomeScreen?.enabled || false}
                          onChange={(e) => {
                            console.log('🔄 PDF welcomeScreen.enabled toggled to:', e.target.checked);
                            handleTypeDataChange({
                              ...typeData,
                              welcomeScreen: { ...(typeData.welcomeScreen || {}), enabled: e.target.checked }
                            })
                            // Force preview update when toggling off
                            if (!e.target.checked) {
                              setTriggerWelcomeAnimation(prev => prev + 1)
                            }
                          }}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    {typeData.welcomeScreen?.enabled && (
                      <div className="ml-6 space-y-3">
                        <div>
                          <label className="block text-xs text-gray-600 mb-1">Bild hochladen</label>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={async (e) => {
                              const file = e.target.files?.[0]
                              if (file) {
                                // Check file size (max 2MB for images)
                                if (file.size > 2 * 1024 * 1024) {
                                  alert('Bild ist zu groß. Bitte verwenden Sie ein Bild unter 2MB.')
                                  return
                                }
                                const url = await handleFileUpload(file, 'image')
                                if (url) {
                                  handleTypeDataChange({
                                    ...typeData,
                                    welcomeScreen: { ...(typeData.welcomeScreen || {}), image: url }
                                  })
                                }
                              }
                            }}
                            className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          />
                          {typeData.welcomeScreen?.image && (
                            <p className="mt-1 text-xs text-green-600">✓ Bild hochgeladen</p>
                          )}
                        </div>
                        {typeData.welcomeScreen?.image && (
                          <div className="flex items-center gap-4">
                            <div className="flex-1">
                              <label className="block text-xs text-gray-600 mb-1">
                                Anzeigedauer: {typeData.welcomeScreen?.duration || 3} Sekunden
                              </label>
                              <input
                                type="range"
                                min="1"
                                max="5"
                                step="1"
                                value={typeData.welcomeScreen?.duration || 3}
                                onChange={(e) => handleTypeDataChange({
                                  ...typeData,
                                  welcomeScreen: { ...(typeData.welcomeScreen || {}), duration: parseInt(e.target.value) }
                                })}
                                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                              />
                            </div>
                            <button
                              type="button"
                              onClick={handleAnimationClick}
                              disabled={isAnimating}
                              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <Play className={`w-3 h-3 ${isAnimating ? 'animate-pulse' : ''}`} />
                              {isAnimating ? 'Läuft...' : 'Animation'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  {/* Password Protection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-700">Passwortschutz</span>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={!!typeData.password}
                          onChange={(e) => handleTypeDataChange({ ...typeData, password: e.target.checked ? '' : null })}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                    {typeData.password !== null && typeData.password !== undefined && (
                      <input
                        type="password"
                        value={typeData.password}
                        onChange={(e) => handleTypeDataChange({ ...typeData, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        placeholder="Passwort eingeben"
                      />
                    )}
                  </div>
                </div>
              </div>
            </details>
          </div>
        )
        
      case 'multishop':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: Shop Verwaltung (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsMultiShopExpanded(!isMultiShopExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Package className="w-5 h-5" />
                    Shop Verwaltung
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isMultiShopExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isMultiShopExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-6">
                    
                    {/* Book Information Section */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Buchinformationen
                      </h4>
                      
                      <div className="grid grid-cols-[120px_1fr] gap-4">
                        {/* Cover Upload on left */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cover
                          </label>
                          <div className="relative">
                            {selectedBook?.cover_url || typeData.coverUrl ? (
                              <div className="relative group">
                                <img 
                                  src={selectedBook?.cover_url || typeData.coverUrl} 
                                  alt="Cover" 
                                  className="w-full h-[180px] object-cover rounded-lg shadow-sm" 
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleTypeDataChange({ ...typeData, coverUrl: '' })
                                    setSelectedBook(null)
                                  }}
                                  className="absolute inset-0 bg-black bg-opacity-50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                >
                                  <span className="text-xs">Eigenes Cover verwenden</span>
                                </button>
                              </div>
                            ) : (
                              <label className="block w-full h-[180px] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                      const url = await handleFileUpload(file, 'image')
                                      if (url) {
                                        handleTypeDataChange({ ...typeData, coverUrl: url })
                                      }
                                    }
                                  }}
                                  className="sr-only"
                                  disabled={uploadingFile}
                                />
                                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                  <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span className="text-xs">Cover hochladen</span>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                        
                        {/* Book details on right */}
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Buchtitel *
                            </label>
                            <input
                              type="text"
                              value={typeData.bookTitle}
                              onChange={(e) => handleTypeDataChange({ ...typeData, bookTitle: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Autor *
                            </label>
                            <input
                              type="text"
                              value={typeData.author}
                              onChange={(e) => handleTypeDataChange({ ...typeData, author: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              ISBN
                            </label>
                            <input
                              type="text"
                              value={typeData.isbn}
                              onChange={(e) => handleTypeDataChange({ ...typeData, isbn: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              placeholder="978-3-12345-678-9"
                            />
                          </div>
                        </div>
                      </div>
                      
                      {/* Typography Section - integrated like in gameplan */}
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Typografie
                        </label>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Überschrift-Schriftart
                            </label>
                            <select
                              value={typeData.fonts?.heading || 'Inter'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <FontOptions />
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Fließtext-Schriftart
                            </label>
                            <select
                              value={typeData.fonts?.body || 'Inter'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                fonts: { ...(typeData.fonts || {}), body: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <FontOptions />
                            </select>
                          </div>
                        </div>
                        
                        {/* Text Size Selection */}
                        <div className="mt-4 grid grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Titel-Textgröße
                            </label>
                            <select
                              value={typeData.textSizes?.bookTitle || 'text-2xl'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                textSizes: { ...(typeData.textSizes || {}), bookTitle: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <option value="text-lg">Klein</option>
                              <option value="text-xl">Mittel</option>
                              <option value="text-2xl">Groß</option>
                              <option value="text-3xl">Sehr groß</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Shop-Textgröße
                            </label>
                            <select
                              value={typeData.textSizes?.shopName || 'text-base'}
                              onChange={(e) => handleTypeDataChange({
                                ...typeData,
                                textSizes: { ...(typeData.textSizes || {}), shopName: e.target.value }
                              })}
                              className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                            >
                              <option value="text-sm">Klein</option>
                              <option value="text-base">Mittel</option>
                              <option value="text-lg">Groß</option>
                              <option value="text-xl">Sehr groß</option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 2: Shop-Links (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsShopLinksExpanded(!isShopLinksExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <ShoppingCart className="w-5 h-5" />
                    Shop-Links
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isShopLinksExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isShopLinksExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
                    <div className="space-y-3">
                      {(typeData.shops || []).map((shop: any, index: number) => (
                        <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                          <div className="space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Shop Name
                                </label>
                                <div className="relative">
                                  <input
                                    type="text"
                                    value={shop.name}
                                    onChange={(e) => {
                                      const newShops = [...(typeData.shops || [])]
                                      newShops[index].name = e.target.value
                                      // Auto-detect shop logo
                                      const shopConfig = getShopConfig(e.target.value)
                                      if (shopConfig) {
                                        newShops[index].logo = shopConfig.logo
                                        newShops[index].isLocal = shopConfig.isLocal
                                      }
                                      handleTypeDataChange({ ...typeData, shops: newShops })
                                    }}
                                    onFocus={(e) => {
                                      e.target.setAttribute('list', `shop-suggestions-${index}`)
                                    }}
                                    className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm ${shop.logo ? 'pl-10' : ''}`}
                                    placeholder="z.B. Amazon, Thalia"
                                    list={`shop-suggestions-${index}`}
                                  />
                                  <datalist id={`shop-suggestions-${index}`}>
                                    <option value="Amazon" />
                                    <option value="Thalia" />
                                    <option value="Hugendubel" />
                                    <option value="Weltbild" />
                                    <option value="Osiander" />
                                    <option value="Bücher.de" />
                                    <option value="Lokaler Buchhändler" />
                                  </datalist>
                                  {shop.logo && (
                                    <img 
                                      src={shop.logo} 
                                      alt={shop.name}
                                      className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 object-contain"
                                    />
                                  )}
                                  {shop.isLocal && !shop.logo && (
                                    <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-green-600" />
                                  )}
                                </div>
                              </div>
                              
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-1">
                                  Preis
                                </label>
                                <div className="flex gap-2">
                                  <input
                                    type="text"
                                    value={shop.price}
                                    onChange={(e) => {
                                      const newShops = [...(typeData.shops || [])]
                                      newShops[index].price = e.target.value
                                      handleTypeDataChange({ ...typeData, shops: newShops })
                                    }}
                                    className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                    placeholder="19.99"
                                  />
                                  <select
                                    value={shop.currency || 'EUR'}
                                    onChange={(e) => {
                                      const newShops = [...(typeData.shops || [])]
                                      newShops[index].currency = e.target.value
                                      handleTypeDataChange({ ...typeData, shops: newShops })
                                    }}
                                    className="w-[72px] px-2 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                                  >
                                    <option value="EUR">EUR</option>
                                    <option value="USD">USD</option>
                                    <option value="GBP">GBP</option>
                                  </select>
                                </div>
                              </div>
                            </div>
                          
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-gray-600 mb-1">
                              Shop URL
                            </label>
                            <input
                              type="url"
                              value={shop.url}
                              onChange={(e) => {
                                const newShops = [...(typeData.shops || [])]
                                newShops[index].url = e.target.value
                                handleTypeDataChange({ ...typeData, shops: newShops })
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                              placeholder="https://..."
                            />
                          </div>
                          
                          {(typeData.shops || []).length > 1 && (
                            <div className="mt-3 flex justify-end">
                              <button
                                type="button"
                                onClick={() => {
                                  const newShops = (typeData.shops || []).filter((_: any, i: number) => i !== index)
                                  handleTypeDataChange({ ...typeData, shops: newShops })
                                }}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                      ))}
                    </div>
                    
                    <button
                      type="button"
                      onClick={() => {
                        handleTypeDataChange({
                          ...typeData,
                          shops: [...(typeData.shops || []), { name: '', url: '', price: '', currency: 'EUR' }]
                        })
                      }}
                      className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700 flex items-center justify-center gap-2 transition-colors bg-white"
                    >
                      <Plus className="w-4 h-4" />
                      Shop hinzufügen
                    </button>
                    
                    {/* Open Directly Toggle */}
                    <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="text-sm font-medium text-gray-700">Direkt zum ersten Shop weiterleiten</span>
                          <span className="text-xs text-gray-500 ml-2">(ohne gestaltete Seite)</span>
                        </div>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            checked={typeData.openDirectly || false}
                            onChange={(e) => {
                              console.log('🔄 Multishop openDirectly toggled to:', e.target.checked);
                              handleTypeDataChange({ ...typeData, openDirectly: e.target.checked })
                            }}
                            className="sr-only peer"
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>
                      {typeData.openDirectly && (
                        <p className="text-xs text-yellow-700 mt-2">
                          Wenn aktiviert, werden Nutzer direkt zum ersten Shop in der Liste weitergeleitet.
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 3: Farbpalette (Collapsible) - Copy from Autorenprofil */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Farbpalette
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-6">
                  <ColorSelector
                    primaryColor={customColors.primaryColor}
                    secondaryColor={customColors.secondaryColor}
                    textColor={customColors.textColor}
                    wrapperColor={customColors.wrapperColor}
                    wrapperOpacity={customColors.wrapperOpacity}
                    onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                    onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                    onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                    onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                    onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                    onAllColorsChange={setCustomColors}
                    colorMode={colorMode}
                    onColorModeChange={setColorMode}
                  />
                </div>
              </div>
            </details>
            
            {/* Zuordnung unten entfernt – wird nun global oben angezeigt */}
            
            {/* Section 6: Branding (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {brandingType === 'custom' && userBranding && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Position
                        </label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Textgröße
                        </label>
                        <select
                          value={typeData.textSizes?.branding || 'text-lg'}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="text-sm">Klein</option>
                          <option value="text-base">Mittel</option>
                          <option value="text-lg">Groß</option>
                          <option value="text-xl">Sehr groß</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>
          </div>
        )
        
      case 'author':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: Profilinformationen (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsAuthorProfileExpanded(!isAuthorProfileExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <User className="w-5 h-5" />
                    Profilinformationen
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isAuthorProfileExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isAuthorProfileExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Name (optional)
                  </label>
                  <input
                    type="text"
                    value={typeData.name}
                    onChange={(e) => handleTypeDataChange({ ...typeData, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Titel/Beruf
                  </label>
                  <input
                    type="text"
                    value={typeData.title}
                    onChange={(e) => handleTypeDataChange({ ...typeData, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Autor, Schriftsteller, etc."
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kurze Biografie (optional)
                </label>
                <textarea
                  value={typeData.shortBio}
                  onChange={(e) => handleTypeDataChange({ ...typeData, shortBio: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Eine kurze Beschreibung (2-3 Sätze)"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ausführliche Biografie
                </label>
                <div className="border border-gray-300 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500">
                  <div className="flex items-center gap-0.5 px-2 py-1.5 bg-gray-50 border-b border-gray-200">
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="bio"]') as HTMLTextAreaElement;
                        if (!textarea) return;
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = typeData.bio || '';
                        const selectedText = text.substring(start, end);
                        const newText = text.substring(0, start) + '**' + selectedText + '**' + text.substring(end);
                        handleTypeDataChange({ ...typeData, bio: newText });
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 2, end + 2);
                        }, 0);
                      }}
                      className="p-1.5 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      title="Fett"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 4h8a4 4 0 014 4 4 4 0 01-4 4H6v0m0 0h9a4 4 0 014 4 4 4 0 01-4 4H6v0m0-8v8m0-16v8" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="bio"]') as HTMLTextAreaElement;
                        if (!textarea) return;
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const text = typeData.bio || '';
                        const selectedText = text.substring(start, end);
                        const newText = text.substring(0, start) + '*' + selectedText + '*' + text.substring(end);
                        handleTypeDataChange({ ...typeData, bio: newText });
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 1, end + 1);
                        }, 0);
                      }}
                      className="p-1.5 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      title="Kursiv"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 4h4m0 16h-4m4-16l-4 16" />
                      </svg>
                    </button>
                    <div className="w-px h-5 bg-gray-300 mx-1" />
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.querySelector('textarea[name="bio"]') as HTMLTextAreaElement;
                        if (!textarea) return;
                        const start = textarea.selectionStart;
                        const text = typeData.bio || '';
                        const lines = text.split('\n');
                        let currentPos = 0;
                        let lineIndex = 0;
                        
                        for (let i = 0; i < lines.length; i++) {
                          if (currentPos + lines[i].length >= start) {
                            lineIndex = i;
                            break;
                          }
                          currentPos += lines[i].length + 1;
                        }
                        
                        if (!lines[lineIndex].trim().startsWith('- ')) {
                          lines[lineIndex] = '- ' + lines[lineIndex];
                        } else {
                          lines[lineIndex] = lines[lineIndex].replace(/^- /, '');
                        }
                        
                        const newText = lines.join('\n');
                        handleTypeDataChange({ ...typeData, bio: newText });
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + (lines[lineIndex].startsWith('- ') ? 2 : -2), start + (lines[lineIndex].startsWith('- ') ? 2 : -2));
                        }, 0);
                      }}
                      className="p-1.5 text-gray-700 rounded hover:bg-gray-200 transition-colors"
                      title="Aufzählung"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
                      </svg>
                    </button>
                  </div>
                  <textarea
                    name="bio"
                    value={typeData.bio}
                    onChange={(e) => handleTypeDataChange({ ...typeData, bio: e.target.value })}
                    className="w-full px-3 py-2 border-0 focus:ring-0 focus:outline-none"
                    rows={6}
                    placeholder="Ihre vollständige Biografie (optional)"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profilbild
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={async (e) => {
                    const file = e.target.files?.[0]
                    if (file) {
                      const url = await handleFileUpload(file, 'image')
                      if (url) {
                        handleTypeDataChange({ ...typeData, profileImageUrl: url })
                      }
                    }
                  }}
                  className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                  disabled={uploadingFile}
                />
                {uploadingFile && (
                  <p className="mt-2 text-sm text-gray-500">Wird hochgeladen...</p>
                )}
                {typeData.profileImageUrl && (
                  <div className="mt-2 flex items-center gap-2">
                    <img 
                      src={typeData.profileImageUrl} 
                      alt="Profilbild" 
                      className="w-16 h-16 rounded-full object-cover"
                    />
                    <p className="text-sm text-green-600">✓ Profilbild hochgeladen</p>
                  </div>
                )}
              </div>

              {/* Font Selection - copy from PDF */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Schriftart
                </label>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Überschrift-Schriftart
                    </label>
                    <select
                      value={typeData.fonts?.heading || 'Inter'}
                      onChange={(e) => handleTypeDataChange({
                        ...typeData,
                        fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                    >
                      <FontOptions />
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Fließtext-Schriftart
                    </label>
                    <select
                      value={typeData.fonts?.body || 'Inter'}
                      onChange={(e) => handleTypeDataChange({
                        ...typeData,
                        fonts: { ...(typeData.fonts || {}), body: e.target.value }
                      })}
                      className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                    >
                      <FontOptions />
                    </select>
                  </div>
                </div>
                
                {/* Text Size Selection */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Textgröße
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Titel
                      </label>
                      <select
                        value={typeData.textSizes?.name || 'text-2xl'}
                        onChange={(e) => handleTypeDataChange({
                          ...typeData,
                          textSizes: { ...(typeData.textSizes || {}), name: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                      >
                        <option value="text-lg">Klein</option>
                        <option value="text-xl">Mittel</option>
                        <option value="text-2xl">Groß</option>
                        <option value="text-3xl">Sehr groß</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Beschreibung
                      </label>
                      <select
                        value={typeData.textSizes?.bio || 'text-base'}
                        onChange={(e) => handleTypeDataChange({
                          ...typeData,
                          textSizes: { ...(typeData.textSizes || {}), bio: e.target.value }
                        })}
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                      >
                        <option value="text-xs">Sehr klein</option>
                        <option value="text-sm">Klein</option>
                        <option value="text-base">Mittel</option>
                        <option value="text-lg">Groß</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 2: Farbpalette (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Farbpalette
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-6">
                  {/* Color Selector (includes color mode toggle) */}
                  <ColorSelector
                    primaryColor={customColors.primaryColor}
                    secondaryColor={customColors.secondaryColor}
                    textColor={customColors.textColor}
                    wrapperColor={customColors.wrapperColor}
                    wrapperOpacity={customColors.wrapperOpacity}
                    onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                    onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                    onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                    onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                    onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                    onAllColorsChange={setCustomColors}
                    colorMode={colorMode}
                    onColorModeChange={setColorMode}
                  />
                </div>
              </div>
            </details>
            
            {/* Section 3: Branding (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  {/* Branding Selector */}
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {/* Company Branding Position and Text Size */}
                  {brandingType === 'custom' && userBranding && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Position
                        </label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Textgröße
                        </label>
                        <select
                          value={typeData.textSizes?.branding || 'text-lg'}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="text-sm">Klein</option>
                          <option value="text-base">Mittel</option>
                          <option value="text-lg">Groß</option>
                          <option value="text-xl">Sehr groß</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>
            
            {/* Section 4: Soziale Netzwerke (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Soziale Netzwerke</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-3">
                  <div className="grid grid-cols-1 gap-3">
                    {[
                      { key: 'website', icon: Globe, name: 'Website' },
                      { key: 'instagram', icon: Instagram, name: 'Instagram' },
                      { key: 'facebook', icon: Facebook, name: 'Facebook' },
                      { key: 'twitter', icon: XIcon, name: 'X' },
                      { key: 'linkedin', icon: Linkedin, name: 'LinkedIn' },
                      { key: 'youtube', icon: Youtube, name: 'YouTube' }
                    ].map((platform) => (
                      <div key={platform.key} className="flex items-center space-x-3">
                        <platform.icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
                        <input
                          type="url"
                          placeholder={`${platform.name} URL`}
                          value={typeData.socialMedia?.[platform.key] || ''}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            socialMedia: { ...(typeData.socialMedia || {}), [platform.key]: e.target.value }
                          })}
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </details>


            {/* Section 6: Auszeichnungen & Preise (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Award className="w-4 h-4" />
                  Auszeichnungen & Preise
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
              
              <div className="flex gap-2">
                <input
                  type="text"
                  value={typeData.newAward || ''}
                  onChange={(e) => handleTypeDataChange({ ...typeData, newAward: e.target.value })}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      if (typeData.newAward?.trim()) {
                        handleTypeDataChange({
                          ...typeData,
                          awards: [...(typeData.awards || []), typeData.newAward.trim()],
                          newAward: ''
                        })
                      }
                    }
                  }}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="z.B. Deutscher Buchpreis 2023"
                />
                <button
                  type="button"
                  onClick={() => {
                    if (typeData.newAward?.trim()) {
                      handleTypeDataChange({
                        ...typeData,
                        awards: [...(typeData.awards || []), typeData.newAward.trim()],
                        newAward: ''
                      })
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Hinzufügen
                </button>
              </div>

              {typeData.awards && typeData.awards.length > 0 && (
                <div className="space-y-2">
                  {typeData.awards.map((award: string, index: number) => (
                    <div key={index} className="flex items-center justify-between bg-gray-50 px-3 py-2 rounded">
                      <span>{award}</span>
                      <button
                        type="button"
                        onClick={() => handleTypeDataChange({
                          ...typeData,
                          awards: typeData.awards.filter((_: string, i: number) => i !== index)
                        })}
                        className="text-red-600 hover:text-red-700"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
                </div>
              </div>
            </details>

          </div>
        )
        
      case 'sample':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: Leseprobe Details (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsLeseprobeExpanded(!isLeseprobeExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Book className="w-5 h-5" />
                    Leseprobe Details
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isLeseprobeExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isLeseprobeExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-6">
                    
                    {/* Buch & Leseprobe Wrapper */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Book className="w-4 h-4" />
                        Buch & Leseprobe
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Buchtitel <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={typeData.title}
                            onChange={(e) => handleTypeDataChange({ ...typeData, title: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Autor(en) <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={typeData.author}
                            onChange={(e) => handleTypeDataChange({ ...typeData, author: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            required
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Leseprobe PDF <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={async (e) => {
                            const file = e.target.files?.[0]
                            if (file) {
                              const url = await handleFileUpload(file, 'pdf')
                              if (url) {
                                handleTypeDataChange({ ...typeData, pdfUrl: url })
                              }
                            }
                          }}
                          className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                          disabled={uploadingFile}
                        />
                        {uploadingFile && (
                          <p className="mt-2 text-sm text-gray-500">Wird hochgeladen...</p>
                        )}
                        {typeData.pdfUrl && (
                          <p className="mt-2 text-sm text-green-600">✓ PDF hochgeladen</p>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            ISBN
                          </label>
                          <input
                            type="text"
                            value={typeData.isbn || ''}
                            onChange={(e) => handleTypeDataChange({ ...typeData, isbn: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Verlag
                          </label>
                          <input
                            type="text"
                            value={typeData.publisher || ''}
                            onChange={(e) => handleTypeDataChange({ ...typeData, publisher: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>

                      {/* Cover Selection - Book Cover vs Upload */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Buchcover
                        </label>
                        <div className="relative">
                          {selectedBook?.cover_url || typeData.coverUrl ? (
                            <div className="relative group">
                              <img
                                src={selectedBook?.cover_url || typeData.coverUrl}
                                alt="Cover"
                                className="w-32 h-48 object-cover rounded-lg shadow-sm mx-auto"
                              />
                              <button
                                type="button"
                                onClick={() => {
                                  handleTypeDataChange({ ...typeData, coverUrl: '' })
                                  setSelectedBook(null)
                                }}
                                className="absolute inset-0 bg-black bg-opacity-50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                              >
                                <span className="text-xs">Eigenes Cover verwenden</span>
                              </button>
                            </div>
                          ) : (
                            <label className="w-32 h-48 mx-auto flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0]
                                  if (file) {
                                    const url = await handleFileUpload(file, 'image')
                                    if (url) {
                                      handleTypeDataChange({ ...typeData, coverUrl: url })
                                    }
                                  }
                                }}
                                className="sr-only"
                                disabled={uploadingFile}
                              />
                              <div className="flex flex-col items-center">
                                <svg className="w-8 h-8 text-gray-400 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="text-xs text-gray-600">Cover hochladen</span>
                              </div>
                            </label>
                          )}
                        </div>
                        {selectedBook && selectedBook.cover_url && !typeData.coverUrl && (
                          <p className="text-xs text-green-600 text-center mt-2">
                            ✓ Cover aus Buchzuordnung übernommen
                          </p>
                        )}
                        {typeData.coverUrl && !selectedBook?.cover_url && (
                          <p className="text-xs text-blue-600 text-center mt-2">
                            ✓ Eigenes Cover hochgeladen
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Leseprobe Einstellungen Wrapper */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Leseprobe Einstellungen
                      </h4>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Genre
                          </label>
                          <input
                            type="text"
                            value={typeData.genre || ''}
                            onChange={(e) => handleTypeDataChange({ ...typeData, genre: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="z.B. Roman, Krimi, Sachbuch"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Seitenzahl
                          </label>
                          <input
                            type="number"
                            value={typeData.pages || ''}
                            onChange={(e) => handleTypeDataChange({ ...typeData, pages: parseInt(e.target.value) || 0 })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            min="1"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Beschreibung
                        </label>
                        <textarea
                          value={typeData.description || ''}
                          onChange={(e) => handleTypeDataChange({ ...typeData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          rows={3}
                          placeholder="Kurze Beschreibung der Leseprobe..."
                        />
                      </div>
                      
                      <div className="space-y-3">
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={typeData.emailGateEnabled}
                            onChange={(e) => handleTypeDataChange({ ...typeData, emailGateEnabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium">
                              <Mail className="w-4 h-4 inline mr-1" />
                              E-Mail-Adresse erforderlich
                            </span>
                            <p className="text-xs text-gray-500">
                              Leser müssen ihre E-Mail angeben, um die Leseprobe zu sehen
                            </p>
                          </div>
                        </label>
                        
                        <label className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={typeData.downloadEnabled}
                            onChange={(e) => handleTypeDataChange({ ...typeData, downloadEnabled: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          <div>
                            <span className="text-sm font-medium">
                              <Download className="w-4 h-4 inline mr-1" />
                              Download erlauben
                            </span>
                            <p className="text-xs text-gray-500">
                              Leser können die PDF herunterladen
                            </p>
                          </div>
                        </label>
                      </div>
                    </div>
                    
                    {/* Typography Section - integrated like in multishop */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Typografie
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Überschrift-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.heading || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Fließtext-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.body || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), body: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                      </div>
                      
                      {/* Text Size Selection */}
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Buchtitel-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.title || 'text-2xl'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), title: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-lg">Klein</option>
                            <option value="text-xl">Mittel</option>
                            <option value="text-2xl">Groß</option>
                            <option value="text-3xl">Sehr groß</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Beschreibung-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.description || 'text-base'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), description: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-sm">Klein</option>
                            <option value="text-base">Mittel</option>
                            <option value="text-lg">Groß</option>
                            <option value="text-xl">Sehr groß</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Section 2: Farbpalette (Collapsible) - Exact copy from Author Profile */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Farbpalette
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-6">
                  {/* Color Selector (includes color mode toggle) */}
                  <ColorSelector
                    primaryColor={customColors.primaryColor}
                    secondaryColor={customColors.secondaryColor}
                    textColor={customColors.textColor}
                    wrapperColor={customColors.wrapperColor}
                    wrapperOpacity={customColors.wrapperOpacity}
                    onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                    onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                    onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                    onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                    onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                    onAllColorsChange={setCustomColors}
                    colorMode={colorMode}
                    onColorModeChange={setColorMode}
                  />
                </div>
              </div>
            </details>

            {/* Section 3: Branding (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  {/* Branding Selector */}
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {/* Company Branding Position and Text Size */}
                  {brandingType === 'custom' && userBranding && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Position
                        </label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Textgröße
                        </label>
                        <select
                          value={typeData.textSizes?.branding || 'text-base'}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="text-sm">Klein</option>
                          <option value="text-base">Mittel</option>
                          <option value="text-lg">Groß</option>
                          <option value="text-xl">Sehr groß</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>
          </div>
        )
      
      case 'website':
        return (
                  <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: Website Einstellungen (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsWebsiteExpanded(!isWebsiteExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Website Einstellungen
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isWebsiteExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isWebsiteExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-6">
                    
                    {/* Website Options Wrapper */}
                    <div className="space-y-4">
                      {/* Title */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Titel <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="text"
                          value={commonData.title}
                          onChange={(e) => handleCommonDataChange({ ...commonData, title: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="Titel für die Website"
                          required
                          data-testid="qr-website-title"
                        />
                      </div>
                      
                      {/* Website URL */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Website URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={commonData.target_url}
                          onChange={(e) => handleCommonDataChange({ ...commonData, target_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          placeholder="https://ihre-website.de"
                          required
                          data-testid="qr-website-url"
                        />
                      </div>
                      
                      {/* Description */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Beschreibung
                        </label>
                        <textarea
                          value={commonData.description}
                          onChange={(e) => handleCommonDataChange({ ...commonData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          rows={2}
                          placeholder="Kurze Beschreibung der Website (optional)"
                          data-testid="qr-website-description"
                        />
                      </div>
                      
                      {/* Open Directly Toggle */}
                      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <span className="text-sm font-medium text-gray-700">Website direkt öffnen</span>
                            <span className="text-xs text-gray-500 ml-2">(ohne gestaltete Seite)</span>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={typeData.openDirectly || false}
                              onChange={(e) => {
                                console.log('🔄 Website openDirectly toggled to:', e.target.checked);
                                handleTypeDataChange({ ...typeData, openDirectly: e.target.checked })
                              }}
                              className="sr-only peer"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                          </label>
                        </div>
                        {typeData.openDirectly && (
                          <p className="text-xs text-yellow-700 mt-2">
                            Wenn aktiviert, werden Nutzer direkt zur Website weitergeleitet.
                          </p>
                        )}
                      </div>
                    </div>
                    
                    {/* Anzeigeoptionen Wrapper - only show if NOT openDirectly */}
                    {!typeData.openDirectly && (
                      <div className="space-y-4">
                        <h4 className="text-sm font-medium text-gray-700">Anzeigeoptionen</h4>
                        
                        {/* Favicon Upload */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Favicon
                          </label>
                          <div className="flex items-center gap-4">
                            {typeData.favicon ? (
                              <div className="relative">
                                <img 
                                  src={typeData.favicon} 
                                  alt="Favicon" 
                                  className="w-12 h-12 object-contain border border-gray-200 rounded" 
                                />
                                <button
                                  type="button"
                                  onClick={() => handleTypeDataChange({ ...typeData, favicon: '' })}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <label className="flex items-center justify-center w-12 h-12 border-2 border-dashed border-gray-300 rounded cursor-pointer hover:border-gray-400">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                      const url = await handleFileUpload(file, 'image')
                                      if (url) {
                                        handleTypeDataChange({ ...typeData, favicon: url })
                                      }
                                    }
                                  }}
                                  className="hidden"
                                />
                                <Upload className="w-5 h-5 text-gray-400" />
                              </label>
                            )}
                            <span className="text-xs text-gray-500">Optional: Website-Icon</span>
                          </div>
                        </div>
                        
                        {/* Button Text */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Button Text
                          </label>
                          <input
                            type="text"
                            value={typeData.buttonText || ''}
                            onChange={(e) => handleTypeDataChange({ ...typeData, buttonText: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                            placeholder="Website besuchen"
                          />
                        </div>
                        
                        {/* Font Selection */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Schriftart
                          </label>
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Überschrift-Schriftart
                              </label>
                              <select
                                value={typeData.fonts?.heading || 'Inter'}
                                onChange={(e) => handleTypeDataChange({
                                  ...typeData,
                                  fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                                })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                              >
                                <FontOptions />
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                Text-Schriftart
                              </label>
                              <select
                                value={typeData.fonts?.body || 'Inter'}
                                onChange={(e) => handleTypeDataChange({
                                  ...typeData,
                                  fonts: { ...(typeData.fonts || {}), body: e.target.value }
                                })}
                                className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                              >
                                <FontOptions />
                              </select>
                            </div>
                          </div>
                          
                          {/* Text Sizes */}
                          <div className="mt-4">
                            <label className="block text-xs font-medium text-gray-600 mb-2">Textgrößen</label>
                            
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Titel</label>
                                <select
                                  value={typeData.textSizes?.title || 'text-2xl'}
                                  onChange={(e) => handleTypeDataChange({
                                    ...typeData,
                                    textSizes: { ...(typeData.textSizes || {}), title: e.target.value }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                >
                                  <option value="text-xl">Klein</option>
                                  <option value="text-2xl">Normal</option>
                                  <option value="text-3xl">Groß</option>
                                  <option value="text-4xl">Sehr groß</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">Beschreibung</label>
                                <select
                                  value={typeData.textSizes?.description || 'text-base'}
                                  onChange={(e) => handleTypeDataChange({
                                    ...typeData,
                                    textSizes: { ...(typeData.textSizes || {}), description: e.target.value }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                >
                                  <option value="text-sm">Klein</option>
                                  <option value="text-base">Normal</option>
                                  <option value="text-lg">Groß</option>
                                </select>
                              </div>
                              
                              <div>
                                <label className="block text-xs text-gray-500 mb-1">URL</label>
                                <select
                                  value={typeData.textSizes?.url || 'text-sm'}
                                  onChange={(e) => handleTypeDataChange({
                                    ...typeData,
                                    textSizes: { ...(typeData.textSizes || {}), url: e.target.value }
                                  })}
                                  className="w-full px-2 py-1 border border-gray-300 rounded text-xs bg-white"
                                >
                                  <option value="text-xs">Sehr klein</option>
                                  <option value="text-sm">Klein</option>
                                  <option value="text-base">Normal</option>
                                </select>
                    </div>
                  </div>

                  
                </div>
              </div>
            </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 2: Farbpalette (Collapsible) - only show if NOT openDirectly */}
            {!typeData.openDirectly && (
              <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                  <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Farbpalette
                  </span>
                  <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-6">
                    <ColorSelector
                      primaryColor={customColors.primaryColor}
                      secondaryColor={customColors.secondaryColor}
                      textColor={customColors.textColor}
                      wrapperColor={customColors.wrapperColor}
                      wrapperOpacity={customColors.wrapperOpacity}
                      onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                      onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                      onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                      onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                      onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                      onAllColorsChange={setCustomColors}
                      colorMode={colorMode}
                      onColorModeChange={setColorMode}
                    />
                  </div>
                </div>
              </details>
            )}
            
            {/* Section 3: Branding (Collapsible) - only show if NOT openDirectly */}
            {!typeData.openDirectly && (
              <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
                <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                  <span className="text-base font-medium text-gray-900">Branding</span>
                  <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
                </summary>
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
                    <BrandingSelector
                      value={brandingType}
                      onChange={setBrandingType}
                    />
                    
                    {brandingType === 'custom' && userBranding && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Position</label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                    )}
                  </div>
                </div>
              </details>
            )}
          </div>
        )
        
      case 'spotify':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: Spotify Playlist (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsSpotifyExpanded(!isSpotifyExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Music className="w-5 h-5" />
                    Spotify Playlist
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isSpotifyExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isSpotifyExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
                    
                    {/* Book Information Section - COPY FROM MULTISHOP */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Book className="w-4 h-4" />
                        Buchinformationen
                      </h4>
                      
                      <div className="grid grid-cols-[auto,1fr] gap-4">
                        {/* Book Cover - LEFT SIDE */}
                        <div className="w-24">
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Buchcover
                          </label>
                          <div className="relative">
                            {typeData.coverUrl ? (
                              <div className="relative">
                                <img src={typeData.coverUrl} alt="Buchcover" className="w-24 h-32 object-cover rounded" />
                                <button 
                                  type="button"
                                  onClick={() => handleTypeDataChange({ ...typeData, coverUrl: null })}
                                  className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                >
                                  <X className="w-3 h-3" />
                                </button>
                              </div>
                            ) : (
                              <div className="w-24 h-32 border-2 border-dashed border-gray-300 rounded flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors">
                                <input 
                                  type="file" 
                                  accept="image/*" 
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                      const url = await handleFileUpload(file, 'image')
                                      if (url) {
                                        handleTypeDataChange({ ...typeData, coverUrl: url })
                                      }
                                    }
                                  }}
                                  className="hidden"
                                  id="spotify-cover-upload"
                                  disabled={uploadingFile}
                                />
                                <label htmlFor="spotify-cover-upload" className="cursor-pointer text-center p-2">
                                  <Upload className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                                  <p className="text-xs text-gray-500">Cover hochladen</p>
                                </label>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        {/* Book Details - RIGHT SIDE */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Buchtitel <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={typeData.bookTitle || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, bookTitle: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Autor(en) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={typeData.author || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, author: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              ISBN
                            </label>
                            <input
                              type="text"
                              value={typeData.isbn || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, isbn: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Spotify Details Section */}
                    <div className="space-y-4 mt-6">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Music className="w-4 h-4 text-green-600" />
                        Playlist Details
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Spotify URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={commonData.target_url}
                          onChange={(e) => handleCommonDataChange({ ...commonData, target_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          placeholder="https://open.spotify.com/playlist/..."
                          required
                          data-testid="qr-spotify-url"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Beschreibung
                        </label>
                        <textarea
                          value={typeData.description || ''}
                          onChange={(e) => handleTypeDataChange({ ...typeData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Begleitende Musik zum Buch..."
                        />
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          id="showBookCover"
                          checked={typeData.showBookCover || false}
                          onChange={(e) => handleTypeDataChange({ ...typeData, showBookCover: e.target.checked })}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label htmlFor="showBookCover" className="text-sm font-medium text-gray-700">
                          Buchcover auf Spotify-Seite anzeigen
                        </label>
                      </div>
                    </div>
                    
                    {/* Typography Section - COPY EXACT FROM AUTORENPROFIL */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Typografie
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Überschrift-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.heading || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Fließtext-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.body || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), body: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                      </div>
                      
                      {/* Text Size Selection */}
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Titel-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.bookTitle || 'text-2xl'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), bookTitle: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-lg">Klein</option>
                            <option value="text-xl">Mittel</option>
                            <option value="text-2xl">Groß</option>
                            <option value="text-3xl">Sehr groß</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Beschreibung-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.description || 'text-base'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), description: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-sm">Klein</option>
                            <option value="text-base">Mittel</option>
                            <option value="text-lg">Groß</option>
                            <option value="text-xl">Sehr groß</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 2: Farbpalette (Collapsible) - Copy from Autorenprofil */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Farbpalette
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-6">
                  <ColorSelector
                    primaryColor={customColors.primaryColor}
                    secondaryColor={customColors.secondaryColor}
                    textColor={customColors.textColor}
                    wrapperColor={customColors.wrapperColor}
                    wrapperOpacity={customColors.wrapperOpacity}
                    onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                    onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                    onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                    onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                    onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                    onAllColorsChange={setCustomColors}
                    colorMode={colorMode}
                    onColorModeChange={setColorMode}
                  />
                </div>
              </div>
            </details>
            
            {/* Zuordnung unten entfernt – wird nun global oben angezeigt */}
            
            {/* Section 4: Branding (Collapsible) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {brandingType === 'custom' && userBranding && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Position
                        </label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Textgröße
                        </label>
                        <select
                          value={typeData.textSizes?.branding || 'text-lg'}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="text-sm">Klein</option>
                          <option value="text-base">Mittel</option>
                          <option value="text-lg">Groß</option>
                          <option value="text-xl">Sehr groß</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>
            
            {/* Section 5: Erweiterte Einstellungen - aktuell ohne Player Theme */}
          </div>
        )
        
      case 'youtube':
        return (
          <div className="space-y-3">
            {assignmentTop}
            {/* Section 1: YouTube Video (Collapsible, Initially Expanded) */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div 
                className="px-6 py-4 bg-white cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => setIsYouTubeExpanded(!isYouTubeExpanded)}
              >
                <div className="flex items-center justify-between">
                  <h3 className="text-base font-semibold text-gray-900 flex items-center gap-2">
                    <Youtube className="w-5 h-5" />
                    YouTube Video
                  </h3>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${isYouTubeExpanded ? 'transform rotate-180' : ''}`} 
                  />
                </div>
              </div>
              {isYouTubeExpanded && (
                <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                  <div className="pt-4 space-y-4">
                    
                    {/* Book Information Section - COPY FROM MULTISHOP */}
                    <div className="space-y-4">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Book className="w-4 h-4" />
                        Buchinformationen
                      </h4>
                      
                      <div className="grid grid-cols-[120px_1fr] gap-4">
                        {/* Book Cover - LEFT SIDE */}
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Cover
                          </label>
                          <div className="relative">
                            {selectedBook?.cover_url || typeData.coverUrl ? (
                              <div className="relative group">
                                <img 
                                  src={selectedBook?.cover_url || typeData.coverUrl} 
                                  alt="Cover" 
                                  className="w-full h-[180px] object-cover rounded-lg shadow-sm" 
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    handleTypeDataChange({ ...typeData, coverUrl: '' })
                                    setSelectedBook(null)
                                  }}
                                  className="absolute inset-0 bg-black bg-opacity-50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-lg"
                                >
                                  <span className="text-xs">Eigenes Cover verwenden</span>
                                </button>
                              </div>
                            ) : (
                              <label className="block w-full h-[180px] border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-gray-400 transition-colors">
                                <input
                                  type="file"
                                  accept="image/*"
                                  onChange={async (e) => {
                                    const file = e.target.files?.[0]
                                    if (file) {
                                      const url = await handleFileUpload(file, 'image')
                                      if (url) {
                                        handleTypeDataChange({ ...typeData, coverUrl: url })
                                      }
                                    }
                                  }}
                                  className="sr-only"
                                  disabled={uploadingFile}
                                />
                                <div className="h-full flex flex-col items-center justify-center text-gray-400">
                                  <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                  </svg>
                                  <span className="text-xs">Cover hochladen</span>
                                </div>
                              </label>
                            )}
                          </div>
                        </div>
                        
                        {/* Book Details - RIGHT SIDE */}
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Buchtitel <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={typeData.bookTitle || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, bookTitle: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              Autor(en) <span className="text-red-500">*</span>
                            </label>
                            <input
                              type="text"
                              value={typeData.author || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, author: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                              required
                            />
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                              ISBN
                            </label>
                            <input
                              type="text"
                              value={typeData.isbn || ''}
                              onChange={(e) => handleTypeDataChange({ ...typeData, isbn: e.target.value })}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* YouTube Details Section */}
                    <div className="space-y-4 mt-6">
                      <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
                        <Youtube className="w-4 h-4 text-red-600" />
                        Video Details
                      </h4>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          YouTube URL <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="url"
                          value={commonData.target_url || ''}
                          onChange={(e) => handleCommonDataChange({ ...commonData, target_url: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          placeholder="https://youtube.com/watch?v=..."
                          required
                          data-testid="qr-youtube-url"
                        />
                      </div>
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Beschreibung
                        </label>
                        <textarea
                          value={typeData.description || ''}
                          onChange={(e) => handleTypeDataChange({ ...typeData, description: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                          rows={2}
                          placeholder="Begleitendes Video zum Buch..."
                        />
                      </div>
                    </div>
                    
                    {/* Typography Section - COPY EXACT FROM AUTORENPROFIL */}
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Typografie
                      </label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Überschrift-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.heading || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), heading: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Fließtext-Schriftart
                          </label>
                          <select
                            value={typeData.fonts?.body || 'Inter'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              fonts: { ...(typeData.fonts || {}), body: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <FontOptions />
                          </select>
                        </div>
                      </div>
                      
                      {/* Text Size Selection */}
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Titel-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.bookTitle || 'text-2xl'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), bookTitle: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-lg">Klein</option>
                            <option value="text-xl">Mittel</option>
                            <option value="text-2xl">Groß</option>
                            <option value="text-3xl">Sehr groß</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            Beschreibung-Textgröße
                          </label>
                          <select
                            value={typeData.textSizes?.description || 'text-base'}
                            onChange={(e) => handleTypeDataChange({
                              ...typeData,
                              textSizes: { ...(typeData.textSizes || {}), description: e.target.value }
                            })}
                            className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                          >
                            <option value="text-sm">Klein</option>
                            <option value="text-base">Mittel</option>
                            <option value="text-lg">Groß</option>
                            <option value="text-xl">Sehr groß</option>
                          </select>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Section 2: Farbpalette (Copy-Paste from Autorenprofil) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900 flex items-center gap-2">
                  <Palette className="w-4 h-4" />
                  Farbpalette
                </span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-6">
                  <ColorSelector
                    primaryColor={customColors.primaryColor}
                    secondaryColor={customColors.secondaryColor}
                    textColor={customColors.textColor}
                    wrapperColor={customColors.wrapperColor}
                    wrapperOpacity={customColors.wrapperOpacity}
                    onPrimaryColorChange={(color) => setCustomColors({ ...customColors, primaryColor: color })}
                    onSecondaryColorChange={(color) => setCustomColors({ ...customColors, secondaryColor: color })}
                    onTextColorChange={(color) => setCustomColors({ ...customColors, textColor: color })}
                    onWrapperColorChange={(color) => setCustomColors({ ...customColors, wrapperColor: color })}
                    onWrapperOpacityChange={(opacity) => setCustomColors({ ...customColors, wrapperOpacity: opacity })}
                    onAllColorsChange={setCustomColors}
                    colorMode={colorMode}
                    onColorModeChange={setColorMode}
                  />
                </div>
              </div>
            </details>
            
            {/* Zuordnung unten entfernt – wird nun global oben angezeigt */}
            
            {/* Section 4: Branding (Copy-Paste from Autorenprofil) */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Branding</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  <BrandingSelector
                    value={brandingType}
                    onChange={setBrandingType}
                  />
                  
                  {brandingType === 'custom' && userBranding && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Position
                        </label>
                        <select
                          value={typeData.companyPosition || 'bottom'}
                          onChange={(e) => handleTypeDataChange({ ...typeData, companyPosition: e.target.value })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="top">Oben</option>
                          <option value="bottom">Unten</option>
                        </select>
                      </div>
                      
                      <div>
                        <label className="block text-xs font-medium text-gray-600 mb-1">
                          Textgröße
                        </label>
                        <select
                          value={typeData.textSizes?.branding || 'text-lg'}
                          onChange={(e) => handleTypeDataChange({
                            ...typeData,
                            textSizes: { ...(typeData.textSizes || {}), branding: e.target.value }
                          })}
                          className="w-full px-2 py-1.5 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-xs bg-white"
                        >
                          <option value="text-sm">Klein</option>
                          <option value="text-base">Mittel</option>
                          <option value="text-lg">Groß</option>
                          <option value="text-xl">Sehr groß</option>
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </details>
            
            {/* Section 5: Erweiterte Einstellungen */}
            <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
              <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
                <span className="text-base font-medium text-gray-900">Erweiterte Einstellungen</span>
                <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
              </summary>
              <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
                <div className="pt-4 space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700">Automatische Wiedergabe</label>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={typeData.autoplay || false}
                        onChange={(e) => handleTypeDataChange({ ...typeData, autoplay: e.target.checked })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </div>
            </details>
          </div>
        )
        
      default:
        return null
    }
  }
  
  // QR preview for design tab - will be shown inside mobile mockup
  const qrPreview = (
    <div className="w-full h-full bg-white flex flex-col items-center justify-center p-8">
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Ihr QR-Code</h2>
        <p className="text-gray-600">Scannen Sie den Code mit Ihrer Kamera</p>
      </div>
      
      <div className="bg-white p-6 rounded-lg" style={{
        border: styleConfig.frameStyle !== 'none' ? `${styleConfig.frameSize}px solid ${styleConfig.frameColor}` : 'none',
        borderRadius: styleConfig.frameStyle === 'rounded' ? '20%' : 
                     styleConfig.frameStyle === 'circle' ? '50%' : '0',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
      }}>
        {qrPreviewUrl ? (
          <>
            <img src={qrPreviewUrl} alt="QR Code Preview" className="w-48 h-48" />
            {styleConfig.frameLabel && (
              <p className="text-center mt-4 font-semibold" style={{
                fontSize: `${styleConfig.frameLabelSize}px`,
                color: styleConfig.frameColor
              }}>
                {styleConfig.frameLabel}
              </p>
            )}
          </>
        ) : (
          <div className="w-48 h-48 bg-gray-100 rounded flex items-center justify-center">
            <p className="text-gray-500 text-sm">QR-Code wird generiert...</p>
          </div>
        )}
      </div>
      
      <div className="mt-6 text-center">
        <p className="text-sm text-gray-500">Design in Echtzeit anpassen</p>
      </div>
    </div>
  )

  // Welcome Screen Animation Component
  // Handle animation button click
  const handleAnimationClick = () => {
    if (!isAnimating) {
      setTriggerWelcomeAnimation(prev => prev + 1)
      setIsAnimating(true)
      // Reset after animation duration
      setTimeout(() => {
        setIsAnimating(false)
      }, (typeData.welcomeScreen?.duration || 3) * 1000 + 1000) // Add 1 second for transition
    }
  }

  return (
    <SplitFormLayout
        preview={activeTab === 'content' ? previewComponent : qrPreview}
        previewTitle={activeTab === 'content' ? `${type.charAt(0).toUpperCase() + type.slice(1)} Vorschau` : 'QR-Code Vorschau'}
        className="h-full"
        hideViewControls={activeTab === 'design'}
        hideZoomControls={activeTab === 'design'}
      >
      <div className="h-full flex flex-col">
        <form 
          onSubmit={handleSubmit} 
          className="flex-1 overflow-hidden"
          onClick={(e) => {
            // Prevent any button clicks from submitting the form unless they're submit buttons
            const target = e.target as HTMLElement;
            if (target.tagName === 'BUTTON' && target.getAttribute('type') !== 'submit') {
              console.log('🛑 Preventing form submission from button:', target);
              e.stopPropagation();
            }
          }}
          onKeyDown={(e) => {
            // Prevent Enter key from submitting form except in textareas
            if (e.key === 'Enter' && (e.target as HTMLElement).tagName !== 'TEXTAREA') {
              console.log('🛑 Preventing Enter key submission');
              e.preventDefault();
            }
          }}
        >
          <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as 'content' | 'design')} className="h-full flex flex-col">
            {!hideDesignTab && !hideContentTab && (
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="content">Inhalt</TabsTrigger>
                <TabsTrigger value="design">Design</TabsTrigger>
              </TabsList>
            )}
            
            {!hideContentTab && (
              <TabsContent value="content" className="flex-1 overflow-y-auto space-y-6">
            {/* Step Title */}
            <div>
              <h3 className="text-lg font-semibold mb-4">{isEditMode ? 'QR-Code bearbeiten' : 'Schritt 2: Inhalte definieren'}</h3>
              <p className="text-gray-600 mb-6">{isEditMode ? 'Bearbeiten Sie die Informationen und das Design für Ihren QR-Code.' : 'Geben Sie die Informationen für Ihren QR-Code ein.'}</p>
            </div>
            
            {/* Type Selection */}
            {onTypeChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {/* Simple types in first column */}
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="spotify"
                    checked={type === 'spotify'}
                    onChange={() => onTypeChange('spotify')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Spotify Playlist</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="website"
                    checked={type === 'website'}
                    onChange={() => onTypeChange('website')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Website</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="youtube"
                    checked={type === 'youtube'}
                    onChange={() => onTypeChange('youtube')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">YouTube Video</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="pdf"
                    checked={type === 'pdf'}
                    onChange={() => {
                      if (!canUploadPDF) {
                        alert('PDF-Upload ist nur in kostenpflichtigen Plänen verfügbar. Bitte upgraden Sie Ihren Plan.')
                        return
                      }
                      onTypeChange('pdf')
                    }}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className={`text-sm ${canUploadPDF ? 'text-gray-700' : 'text-gray-400'}`}>
                    PDF Dokument {!canUploadPDF && '(Premium)'}
                  </span>
                </label>
              </div>
              {/* Complex types in second column */}
              <div className="space-y-2">
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="multishop"
                    checked={type === 'multishop'}
                    onChange={() => onTypeChange('multishop')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Multi-Shop (Buchhandel)</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="author"
                    checked={type === 'author'}
                    onChange={() => onTypeChange('author')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Autorenprofil</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="radio"
                    name="qr_type"
                    value="sample"
                    checked={type === 'sample'}
                    onChange={() => onTypeChange('sample')}
                    className="mr-2 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-700">Leseprobe</span>
                </label>
                </div>
              </div>
            </div>
          )}
          
          {/* Common Fields */}
          {renderCommonFields()}
          
          {/* Type-Specific Fields */}
          {renderTypeSpecificFields()}
          
          
          {/* Zuordnung Section (Collapsible) - Only show for types that don't have their own Zuordnung */}
          {!['spotify', 'multishop', 'author', 'sample'].includes(type) && (
          <details className="bg-white rounded-lg border border-gray-200 overflow-hidden group">
            <summary className="px-6 py-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 text-left focus:outline-none focus:bg-gray-50 flex items-center justify-between">
              <span className="text-base font-medium text-gray-900">Zuordnung</span>
              <ChevronRight className="w-4 h-4 text-gray-400 transition-transform duration-200 group-open:rotate-90" />
            </summary>
            <div className="px-6 pb-6 bg-gray-50 border-t border-gray-100">
              <div className="pt-4 space-y-4">
                {/* Book/Campaign Assignment */}
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Buch (optional)
                      </label>
                      <select
                        value={selectedBook}
                        onChange={(e) => setSelectedBook(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Kein Buch zugeordnet</option>
                        {books.map(book => (
                          <option key={book.id} value={book.id}>
                            {book.title} {book.isbn ? `(${book.isbn})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Kampagne (optional)
                      </label>
                      <select
                        value={selectedCampaign}
                        onChange={(e) => setSelectedCampaign(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Keine Kampagne zugeordnet</option>
                        {campaigns.map(campaign => (
                          <option key={campaign.id} value={campaign.id}>
                            {campaign.title}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                
                
                {/* QR Code Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    <QrCode className="w-4 h-4 inline mr-1" />
                    QR-Code Name (intern)
                  </label>
                  <input
                    type="text"
                    value={typeData.qrCodeName || ''}
                    onChange={(e) => handleTypeDataChange({ ...typeData, qrCodeName: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                    placeholder="z.B. Buch XY - Bonusmaterial"
                  />
                </div>
              </div>
            </div>
          </details>
          )}
          
        </TabsContent>
          )}
      
      {!hideDesignTab && (
        <TabsContent value="design" className="flex-1 overflow-y-auto">
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold mb-4">{isEditMode ? 'QR-Code Design bearbeiten' : 'Schritt 3: QR-Code Design wählen'}</h3>
            <p className="text-gray-600 mb-6">Passen Sie das Aussehen Ihres QR-Codes an.</p>
          </div>
          <QRStylePanel 
            config={styleConfig} 
            onChange={(newConfig) => {
              console.log('🎨 UnifiedQRForm - QRStylePanel onChange called with:', newConfig);
              setStyleConfig(newConfig);
            }}
            userBranding={userBranding}
          />
        </div>


      </TabsContent>
      )}
    </Tabs>
  </form>
  
  {/* Navigation and Submit Section */}
  <div className="bg-white px-6 py-3 mt-auto">
    <div className="space-y-2">
        
        {/* Navigation Buttons */}
        <div className="flex gap-3">
          {isEditMode ? (
            // In edit mode, show only Save button
            <button
              type="button"
              onClick={handleSubmit}
              disabled={uploadingFile || isSubmitting}
              className="w-full bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="qr-save"
            >
              {isSubmitting ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Wird gespeichert...
                </>
              ) : uploadingFile ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Wird hochgeladen...
                </>
              ) : (
                'Speichern'
              )}
            </button>
          ) : (
            // In creation mode, show Back and Next buttons
            <>
              {/* Back Button */}
              <button
                type="button"
                onClick={() => {
                  if (activeTab === 'design' && !hideContentTab) {
                    // In normal mode, go back to content tab
                    setActiveTab('content')
                  } else if (onBack) {
                    // Use the provided onBack callback
                    onBack()
                  } else {
                    // Fallback to browser history
                    window.history.back()
                  }
                }}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                data-testid="qr-back"
              >
                <ArrowLeft className="w-4 h-4" />
                Zurück
              </button>
              
              {/* Next/Submit Button */}
              {activeTab === 'content' && !hideDesignTab ? (
                <button
                  type="button"
                  onClick={() => setActiveTab('design')}
                  disabled={uploadingFile}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="qr-next-to-design"
                >
                  Weiter zum Design
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : activeTab === 'content' && hideDesignTab ? (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={uploadingFile}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="qr-continue"
                >
                  Weiter
                  <ArrowRight className="w-4 h-4" />
                </button>
              ) : activeTab === 'design' ? (
                <button
                  type="button"
                  onClick={(e) => {
                    console.log('🔴 Submit button in Design tab clicked!');
                    console.log('🔴 Event:', e);
                    console.log('🔴 isSubmitting:', isSubmitting);
                    if (!isSubmitting && !uploadingFile) {
                      handleSubmit(e);
                    }
                  }}
                  disabled={uploadingFile || isSubmitting}
                  className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  data-testid="qr-submit"
                >
                  {isSubmitting ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Wird erstellt...
                    </>
                  ) : uploadingFile ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Wird hochgeladen...
                    </>
                  ) : (
                    'QR-Code erstellen'
                  )}
                </button>
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>
  </div>
</SplitFormLayout>
  )
} 
